import React, { useEffect, useMemo, useState } from "react";
import DtvTheme, { MOBILE_WIDTH, TABLET_WIDTH, colorPalette, MBL_SIZE, TBL_SIZE } from "@directv-digital/dds.ui.dtv-theme";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";
import LazyLoad from "react-lazyload";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import ChannelLogoChip from "@/shared/ChannelLogoChip";
import RadioButton from "@directv-digital/fusion.atoms.radio-button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CardMedia from "@mui/material/CardMedia";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell"
import { CHANNEL_LINEUP_VIEW, PACKAGE_INDICATORS } from "@directv-digital/dds.services.channel-lineup-provider";
import { defaultBgColors, packageTextColorMap, packageColors, packageColorMap } from "./utils/colorMapping";
import { getPackageColors, getPackageCta, resolveCmprPkgSelection, getPkgIndicator } from "./utils/pkgCalculations";
import { isValidModalSrc } from "../utils/utilityFunctions";
import { getChannelColWidth, getMainColWidths, getEmbeddedSvodColWidth, getStyles } from "./ChnlComparisonGridStyles";

let unqId = 0;
export function uniqId(prefix = "id") {
  // eslint-disable-next-line no-plusplus
  unqId++;
  return `${prefix}${unqId}`;
}

const UpIcon = (props: any) => (
  <svg fill="none" height="24" viewBox="0 0 25 25" width="24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.84711 9.03841C3.84711 8.6233 3.99836 8.20139 4.30743 7.88155C4.92557 7.24868 5.92512 7.24868 6.53668 7.88155L12.1657 13.7203L17.8078 7.86794C18.4259 7.22827 19.4189 7.22827 20.037 7.86794C20.6552 8.50761 20.6552 9.53517 20.037 10.1748L13.7241 16.7213C13.3164 17.1432 12.7509 17.3882 12.1591 17.3882C11.5738 17.3882 11.0017 17.15 10.594 16.7281L4.29431 10.1953C3.98524 9.87542 3.83398 9.46031 3.83398 9.04521"
      fill={props?.mode === 'dark' ? "#ffffff" : "#102641"}
    />
  </svg>
);

const DownIcon = (props: any) => (
  <svg fill="none" height="24" viewBox="0 0 25 24" width="24" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21.1529 15.0163C21.1529 15.4314 21.0016 15.8533 20.6926 16.1731C20.0744 16.806 19.0749 16.806 18.4633 16.1731L12.8343 10.3344L7.1922 16.1867C6.57406 16.8264 5.58109 16.8264 4.96295 16.1867C4.34482 15.5471 4.34482 14.5195 4.96295 13.8798L11.2759 7.3334C11.6836 6.91148 12.2491 6.66651 12.8409 6.66651C13.4262 6.66651 13.9983 6.90467 14.406 7.32658L20.7057 13.8594C21.0148 14.1793 21.166 14.5944 21.166 15.0095"
      fill={props?.mode === 'dark' ? "#ffffff" : "#102641"}
    />
  </svg>
);

// Satellite-only MyFreeDIRECTV caveat, shared by both grid layouts (channel/
// number view + category view) so the condition + markup are computed once
// per call site instead of duplicated verbatim (and can't drift on variant/
// color between the two, as the two copies previously did).
const MyFreeDtvSubheading = ({ id, content, theme }: { id: string; content: string; theme?: any }) => (
  <DtvBox
    id={id}
    sx={{
      // Bleeds past the parent AccordionSummary's own 20px L/R padding
      // (styles.accordionSummary, shared by every category header) so this
      // box's background reaches the true edges instead of sitting inset.
      // The negative margins are what actually escape that padding: width
      // alone gets cancelled here because this is a flex item inside the
      // header's flex/wrap row, so flex-shrink pulls any extra width back to
      // the line. Margins are excluded from shrinking, and outer size stays
      // (100% + 56px) - 56px = 100%, so the row still wraps it to its own line.
      // Right needs 16px more than left: the AccordionSummary is a flex ROW, so
      // .MuiAccordionSummary-content is sized to (root - padding - expandIcon
      // Wrapper), and that chevron (styles.dropDownIcon, 16px) sits in flow —
      // it eats 16px off the right of this box's containing block only.
      boxSizing: "border-box",
      width: "calc(100% + 56px)",
      marginLeft: "-20px",
      marginRight: "-36px",
      flexShrink: 0,
      backgroundColor: colorPalette.neutral[100],
      // L/R padding matches the AccordionSummary's own 20px inset so this
      // text's left edge lines up with the category label's above it.
      paddingTop: DtvTheme.spacing(2),
      paddingBottom: DtvTheme.spacing(1),
      paddingLeft: "20px",
      paddingRight: "20px",
      marginTop: DtvTheme.spacing(2)
    }}
  >
    <DtvText
      content={content}
      sx={{ color: theme && theme?.sectionHeaders?.textColor ? theme?.sectionHeaders?.textColor : colorPalette.jet.main }}
      variant="body3"
    />
  </DtvBox>
);

const _RSN_NOT_AVAILABLE_PACKAGES = [
  "ATT_TV_ENTERTAINMENT",
  "DTV_ENTERTAINMENT",
  "ATT_TV_MYSPORTSBETA"
]

const DEFAULT_ADDON_PROMO_PACKAGES = [
  "ATT_TV_ENTERTAINMENT",
  "DTV_ENTERTAINMENT"
]

const { breakpoints } = DtvTheme;

export interface DtvChannelComparisonGridProps {
  id: string;
  packages: any[];
  channels: any[];
  sortOptions: any[];
  channelCategories: any[];
  isChannelLineUpPage?: boolean;
  commonData: any;
  viewMode: any;
  modalView: boolean;
  edspFlow?: boolean;
  packageCategorySelected?: string;
  packageBgColor?: any;
  customPackage: any[];
  expanded: boolean;
  cmprPkg?: string;
  modalSrc?: string;
  channelsQP?: string;
  lang?: string;
  showCTA?: string;
  showCtaCI?: string;
  customPkgIndex?: any;
  changeViewModeFromNationalTable?: any;
  localMySportsGenreChannels?: any;
  msbNationalRollOut?: any;
  theme?: any;
  embeddedSvods?: any;
  getOffersDTViEmbeddedIncludedSvods?: any;
  getOffersDTVsEmbeddedIncludedSvods?: any;
  isEmbeddedSvodsEnabled?: boolean;
  rsnChnlsObj?: any;
  rsnTierLegalTextObj?: any;
  channelFilter?:any;
  onEmbeddedSvodsFiltered?: (svods: any[]) => void;
  // CATEGORY-view multi-membership map { contentBucketKey: channelId[] } built
  // server-side via bucketChannelsByCategory (mirrors /channel-lineup) so a channel
  // renders under EVERY matching content accordion despite the single scalar
  // channel.category. Structural LOCAL/RSN are NOT in here (the scalar path handles them).
  channelCategoryMembership?: Record<string, number[]>;
}

// eslint-disable-next-line complexity
export function ChnlComparisonGrid(props: DtvChannelComparisonGridProps) {
  try {
    const {
      id = "channel-comparison-grid",
      isChannelLineUpPage = false,
      packages,
      channels,
      channelCategories,
      commonData,
      sortOptions,
      viewMode,
      customPackage,
      customPkgIndex,
      modalView,
      edspFlow,
      modalSrc,
      showCtaCI,
      expanded,
      localMySportsGenreChannels,
      theme,
      msbNationalRollOut: _msbNationalRollOut,
      embeddedSvods,
      getOffersDTViEmbeddedIncludedSvods,
      getOffersDTVsEmbeddedIncludedSvods,
      isEmbeddedSvodsEnabled,
      rsnChnlsObj,
      rsnTierLegalTextObj,
      channelFilter,
      onEmbeddedSvodsFiltered,
      channelCategoryMembership
      // packageBgColor
    } = props;
    // eslint-disable-next-line camelcase
    const channelsQP_lwc = props?.channelsQP?.toLowerCase() || "";
    // Search-collapse: when a channel is actively searched (a chip is selected) in the
    // CATEGORY view, render the Name-view grouping (one Included row + one Add-on row)
    // instead of fanning the channel across every content-category accordion. channelFilter
    // is the selected-chip array (= toolState.channelFilter.selectedChannel) and `channels`
    // is already pre-filtered to the matched subset. Browsing (no chip) keeps content cats.
    const isSearchActive = Array.isArray(channelFilter) && channelFilter.length > 0;
    // const lang_lwc = props?.lang?.toLowerCase() || "";
    // eslint-disable-next-line camelcase
    const pkgCtgySelected_upc = props?.packageCategorySelected?.toUpperCase() || "";
    const showCta = props?.showCTA?.toUpperCase() || "";

    const embeddedSvodsData = useMemo(() => {
      if (!Array.isArray(embeddedSvods) || !Array.isArray(packages) || packages.length === 0) {
        return [];
      }

      const filteredPkgs = packages.reduce((acc, pkg) => {
        const key = pkg?.billingCodeLabel?.toLowerCase?.();
        if (key) {
          acc[key] = pkg?.id;
        }
        return acc;
      }, {});

      const embeddedIncludedSvodsSource = props?.channelsQP?.toUpperCase() === "SATELLITE" ?
        getOffersDTVsEmbeddedIncludedSvods :
        getOffersDTViEmbeddedIncludedSvods;

      const svodsWithPackages = embeddedSvods
        ?.map((item: any) => {
          const packageListSystem = [];
          let epochPackageName = "";
          const offerCode = props?.channelsQP?.toUpperCase() === "SATELLITE" ? item?.offerCodeSatellite : item?.offerCode;

          Object?.keys(filteredPkgs)?.forEach?.((key: string) => {
            embeddedIncludedSvodsSource?.[key]?.forEach?.((svod: any) => {
              if (svod?.code === offerCode && filteredPkgs?.[key]) {
                packageListSystem?.push(filteredPkgs?.[key]);
              }
              if (svod?.code === offerCode && svod?.name) {
                epochPackageName = svod?.name;
              }
            });
          });

          return {
            ...item,
            packageListSystem,
            name: item?.displayName || epochPackageName || item?.name
          };
        })
        ?.filter((svod: any) => svod?.chlupModalData?.disable === false)
        ?.filter?.((svod: any) => svod?.packageListSystem?.length > 0);

      return svodsWithPackages || [];
    }, [embeddedSvods, packages, getOffersDTViEmbeddedIncludedSvods, getOffersDTVsEmbeddedIncludedSvods, props?.channelsQP]);

    /** -----------------------------------------
     * Scroll management and responsive styles */
    // Fix: Initialize with fallback to avoid hydration mismatch
    const [viewportWidth, setViewportWidth] = React.useState(TABLET_WIDTH);
    const isMobileVP = useMemo(() => viewportWidth <= MOBILE_WIDTH, [viewportWidth]);
    const isDesktopVP = useMemo(() => viewportWidth > TABLET_WIDTH, [viewportWidth]);
    const isTabletVP = useMemo(() => (viewportWidth > MOBILE_WIDTH) && (viewportWidth <= TABLET_WIDTH), [viewportWidth]);

    // Update viewport width after hydration to avoid mismatch
    React.useEffect(() => {
      const updateViewportWidth = () => {
        if (typeof window !== 'undefined') {
          const width = screen?.width || window?.parent?.innerWidth || TABLET_WIDTH;
          setViewportWidth(width);
        }
      };

      updateViewportWidth();
      window.addEventListener('resize', updateViewportWidth);

      return () => window.removeEventListener('resize', updateViewportWidth);
    }, []);

    /** -----------------------------------------------
     * Styles re-calculated on viewport size changes */
    let tableHeaderRef: HTMLTableSectionElement;
    const [verticalPkgNamesOn, setVerticalPkgNamesOn] = useState<boolean>(false);
    const [maxPkgNameHeight, setMaxPkgNameHeight] = useState<number>(-1);
    const [tableHeaderOffset, setTableHeaderOffset] = useState<number>(0);
    const [headerHeight, setHeaderHeight] = useState<number>(0);
    const [isSticky, setIsSticky] = useState<boolean>(false);
    const [titleHeight, setTitleHeight] = useState<number>(0);

    // Fix: Calculate titleHeight after mount to avoid hydration mismatch
    React.useEffect(() => {
      if (typeof document !== 'undefined') {
        const el = document.getElementById("channel-lineup-headline");
        if (el) {
          setTitleHeight(el.getBoundingClientRect().height);
        }
      }
    }, [isMobileVP, isTabletVP, isDesktopVP]);

    const tableHeaderLeft = useMemo(() => {
      if (tableHeaderRef && tableHeaderRef.getBoundingClientRect().left > 0) {
        return tableHeaderRef.getBoundingClientRect().left;
      }
      return 0;
    }, [viewportWidth]);

    const updateVerticalHeaders = (thElem: HTMLElement) => {
      const containerWidth = thElem?.querySelectorAll(".package-container")?.[0]?.getBoundingClientRect()?.width;
      const maxPkgTextDim = Array.from(thElem?.querySelectorAll(".package-text-container"))
        .reduce((maxDim, container) => {
          const pkgTextDim = (container.querySelector(".package-name") as HTMLElement)?.getBoundingClientRect();
          const pkgChnlCountDim = (container.querySelector(".package-chnl-count") as HTMLElement)?.getBoundingClientRect();
          const pkgMaxDim = Math.max(
            pkgTextDim?.width, pkgTextDim?.height,
            pkgChnlCountDim?.width, pkgChnlCountDim?.height
          );
          return Math.max(maxDim, pkgMaxDim);
        }, -1);
      setMaxPkgNameHeight(maxPkgTextDim);

      // if on and longest text is shorter than the containerWidth, turn off
      if (verticalPkgNamesOn === true) {
        if ((maxPkgTextDim < containerWidth - 24)) {
          setVerticalPkgNamesOn(false);
        }
        return;
      } // if off and longest text is longer than the containerWidth, turn on
      if (maxPkgTextDim > containerWidth - 24) {
        setVerticalPkgNamesOn(true);
        return;
      }
      setVerticalPkgNamesOn(false);
    }

    const onScrollHandle = () => {
      const turnOn = (window.scrollY > tableHeaderOffset);
      if (turnOn !== isSticky) {
        setIsSticky(turnOn);
      }
    };

    /** - Update table dimensions when the vertical headers switches
     * - Check sticky header
     */
    useEffect(() => {
      // console.log("useEffect verticalPackagesOn flipped", verticalPkgNamesOn);
      const tableHeaderElem = tableHeaderRef || document.getElementById("channels-table-head");
      if (tableHeaderElem) {
        setTableHeaderOffset(pv => (tableHeaderElem?.offsetTop > 0 ? tableHeaderElem?.offsetTop : pv));
        setHeaderHeight(pv => (tableHeaderElem?.clientHeight > 0 ? tableHeaderElem?.clientHeight : pv));
        onScrollHandle();
      }
    }, [verticalPkgNamesOn]);

    /** - Check for vertical headers flip
     *  - update header offset
     *  - check sticky header */
    useEffect(() => {
      // console.log("viewportWidth effect", viewportWidth);
      const tableHeaderElem = tableHeaderRef || document.getElementById("channels-table-head");
      if (tableHeaderElem) {
        updateVerticalHeaders(tableHeaderElem);
        setTableHeaderOffset(pv => (tableHeaderElem?.offsetTop > 0 ? tableHeaderElem?.offsetTop : pv));
        setHeaderHeight(pv => (tableHeaderElem?.clientHeight > 0 ? tableHeaderElem?.clientHeight : pv));
        onScrollHandle();
      }
    }, [viewportWidth]);

    const onWindowResize = () => {
      setViewportWidth(screen?.width || window?.parent?.innerWidth);
    };

    useEffect(() => {
      window.addEventListener("scroll", onScrollHandle);
      window.addEventListener("resize", onWindowResize);
      onWindowResize();
      return () => {
        window.removeEventListener("scroll", onScrollHandle);
        window.removeEventListener("resize", onWindowResize);
      }
    }, []);

    /** -----------------------------------------------
     * Styles re-calculated on viewport size changes */
    const stickyAccordionStyles = useMemo(() => {
      console.log("titleHeight", titleHeight);
      console.log("headerHeight", headerHeight);
      return {
        position: "sticky",
        top: isChannelLineUpPage ? '0px' : `${(titleHeight || 48) + headerHeight}px`,
        left: tableHeaderLeft
      }
    }, [tableHeaderLeft, headerHeight, titleHeight]);

    const stickyHeaderStyles = useMemo(() => ({
      backgroundColor: "#ffffff",
      width: "100%",
      zIndex: 999,
      position: "sticky",
      top: isChannelLineUpPage ? '0px' : `${titleHeight || 48}px`,
      left: tableHeaderLeft
    }), [tableHeaderLeft, headerHeight, titleHeight]);

    const packagesLength = packages?.length || 1;
    const isSinglePkgUseCase = packagesLength === 1;

    const channelColWidthStyle = useMemo(() => getChannelColWidth(isMobileVP, isTabletVP, isSinglePkgUseCase), [isMobileVP, isTabletVP]);

    const embeddedSvodColWidthStyle = useMemo(() => getEmbeddedSvodColWidth(isMobileVP, isTabletVP, isSinglePkgUseCase, packagesLength), [isMobileVP, isTabletVP]);

    const mainColWidthStyle = useMemo(() => getMainColWidths(isMobileVP, isTabletVP, isSinglePkgUseCase, packagesLength), [isMobileVP, isTabletVP]);

    const styles = useMemo(() => getStyles(isChannelLineUpPage, isMobileVP, isTabletVP, isDesktopVP, theme), [isDesktopVP]);

    /** -------------------------------
     * Accordions */
    // MyFreeDIRECTV section: stream shows it for the default/internet feed
    // (package-token driven, see parseChannelsData's isMyFreeDTVEnabled); satellite
    // shows it too, but only for FAST-flagged channels (see isSatelliteFastChannel
    // in channel-compare-tool-page-adapter.ts) — both land on channel.isMyFreeDTV.
    const isSatelliteQP = props?.channelsQP?.toLowerCase?.() === "satellite";
    const chnlNameNumberCategories = [
      {
        key: "INCLUDED_CHANNELS",
        value: "Included channels"
      },
      ...(!(props?.packages?.length === 1 && props?.packages?.[0]?.id === "ATT_TV_MYSPORTSBETA") ? [{
        key: "ADD_ON_CHANNELS",
        value: "Add on channels"
      }] : []),
      ...((props?.channelsQP === '' || props?.channelsQP?.toLowerCase?.() === "internet" || isSatelliteQP) ? [{
        key: "MYFREEDTV_CHANNELS",
        value: "MyFree DIRECTV (Channels available in all packages)"
      }] : [])
    ];
    if (isEmbeddedSvodsEnabled && embeddedSvodsData?.length > 0) {
      chnlNameNumberCategories.unshift({
        key: "INCLUDED_SUBSCRIPTIONS",
        value: "Included subscriptions"
      });
    }
    const chnlNameNumberAccordionArray =
      chnlNameNumberCategories.map((_, index) => (index === 0 ? { expanded: true } : { expanded: false }));
    const [chnlNameNumberAccordion, setChnlNameNumberAccordion] =
      useState(chnlNameNumberAccordionArray);

    const updatedChnlCategories = [
      ...channelCategories,
      ...(!(props?.packages?.length === 1 && props?.packages?.[0]?.id === "ATT_TV_MYSPORTSBETA") ? [{
        key: "ADD_ON_CHANNELS",
        value: "Add on channels"
      }] : []),
      ...((props?.channelsQP === '' || props?.channelsQP?.toLowerCase?.() === "internet" || isSatelliteQP) ? [{
        key: "MYFREEDTV_CHANNELS",
        value: "MyFree DIRECTV (Channels available in all packages)"
      }] : [])
    ];
    if (isEmbeddedSvodsEnabled && embeddedSvodsData?.length > 0) {
      updatedChnlCategories.unshift({
        key: "INCLUDED_SUBSCRIPTIONS",
        value: "Included subscriptions"
      });
    }
    const [latestAccordionOpen, setLatestAccordionOpen] = useState([0]);
    const [expandAccordion, setExpandAccordion] =
      useState(updatedChnlCategories.map((_obj, index) => (index === 0 ? { expanded: true } : { expanded: false })));
    const [defaultSortOption, setDefaultSortOption] = useState(true);
    const [detailsVisible, setDetailsVisible] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const embeddedSvodsInitialPayloadSentRef = React.useRef(false);

    useEffect(() => {
      embeddedSvodsInitialPayloadSentRef.current = false;
    }, [props?.channelsQP]);

    useEffect(() => {
      if (!onEmbeddedSvodsFiltered || embeddedSvodsInitialPayloadSentRef.current) {
        return;
      }
      onEmbeddedSvodsFiltered(embeddedSvodsData || []);
      embeddedSvodsInitialPayloadSentRef.current = true;
    }, [embeddedSvodsData, onEmbeddedSvodsFiltered]);

    useEffect(() => {
      if (expanded) {
        expandAccordion?.map(item => item.expanded = true);
        setExpandAccordion([...expandAccordion]);
      } else if (!expanded) {
        expandAccordion?.forEach((item, index) => {
          item.expanded = !!(index === 0 || index === 1);
        });
        setExpandAccordion([...expandAccordion]);
      }
    }, [expanded]);

    useEffect(() => {
      setChnlNameNumberAccordion(chnlNameNumberAccordionArray);
      if (viewMode === 1) {
        setLatestAccordionOpen([0]);
      } else if (viewMode === 2) {
        setLatestAccordionOpen([0, 1]);
      }
    }, [viewMode]);

    /* Dynamically open all accordions when Filter applies. */
    useEffect(() => {
      if (Array.isArray(channelFilter) && channelFilter.length > 0) {
        setChnlNameNumberAccordion(prevAcc => {
          if (!Array.isArray(prevAcc) || prevAcc.length === 0) {
            return prevAcc;
          }
          return prevAcc.map(() => ({ expanded: true }));
        });
        setExpandAccordion(
          expandAccordion.map(() => ({ expanded: true }))
        );
      }else {
        setChnlNameNumberAccordion(chnlNameNumberAccordionArray);
        setExpandAccordion(expandAccordion);
      }
    }, [channelFilter, viewMode]);

    /** -----------------------
     *  Click Event Handlers */
    const handleAccordionClick = (event, index) => {
      if (event?.target?.id) {
        const isOpening = !expandAccordion[index].expanded;
        if (isOpening) {
          setLatestAccordionOpen(prev => (prev.includes(index) ? prev : [...prev, index]));
        } else {
          setLatestAccordionOpen(prev => prev.filter(i => i !== index));
        }
        setExpandAccordion(prevAcc => prevAcc?.map((item, i) => (index === i ? { expanded: !prevAcc[index].expanded } : item)));

        if (isOpening) {
          setTimeout(() => {
            document.getElementById(`${index}-catergoryValue`)?.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }, 0);
        }
      }
    };
    const handleChnlNameNumberClick = (event, index) => {
      if (event?.target?.id) {
        const isOpening = !chnlNameNumberAccordion?.[index]?.expanded;
        if (isOpening) {
          setLatestAccordionOpen(prev => (prev.includes(index) ? prev : [...prev, index]));
        } else {
          setLatestAccordionOpen(prev => prev.filter(i => i !== index));
        }
        setChnlNameNumberAccordion(prevAcc => {
          if (!Array.isArray(prevAcc)) {
            return prevAcc;
          }
          return prevAcc.map((item, i) => (i === index ? { expanded: !item?.expanded } : item));
        });

        if (isOpening) {
          setTimeout(() => {
            document.getElementById(`${index}-catergoryValue`)?.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }, 0);
        }
      }
    };
    const handleClickChannel = (event, channel) => {
      setSelectedChannel(channel?.chnlNum);
      setDetailsVisible(prevVisible => (channel?.chnlNum === selectedChannel ? !prevVisible : true));
      event?.stopPropagation();
    };

    /**
     * cmprPkg and pkg mapping */
    let cmprPkgIndex;
    let cmprPkgBorderSelected;
    const cmprPkg = props?.cmprPkg ? props?.cmprPkg?.toUpperCase() :
      customPkgIndex !== null ? customPackage?.[0]?.billingCodeLabel?.toUpperCase() : "";

    if (cmprPkg || customPkgIndex !== null) {
      // WAT-11878: pure helper handles the accent-folded name-fallback match
      // AND the colour-map key derivation from the matched package's name —
      // see resolveCmprPkgSelection in ./utils/pkgCalculations for detail.
      const resolved = resolveCmprPkgSelection(cmprPkg, packages, packageColorMap);
      cmprPkgIndex = resolved.index;
      if (cmprPkgIndex !== -1) {
        cmprPkgBorderSelected = packageColors[`borderColor${resolved.packageColorMapValue}Selected`];
      }
    }

    /** ---------------
     *  DOM Elements */
    const primaryDtvRadioButton = {
      label: "Label",
      analytics: {
        tag: "Radiobutton",
        event: "Checked",
        event_name: "Radiobutton"
      },
      mode: "dark" as any,
      variant: "Radio Label" as any,
      ariaLabel: "Sort by",
      radiobuttonoptions: sortOptions && sortOptions.map(op => ({
        label: op.value,
        value: op.key,
        ariaLabel: op.value === "A-Z" ? "Sort A to Z" : `Sort by ${op.value}`,
        checked: defaultSortOption ?
          op.value === "A-Z" :
          op.value === "Category"
      }))
    };

    const getTooltipContent = (text?: any, pkgName?: any) => (
        <DtvBox sx={{ textAlign: "center" }}>
          <DtvText content={text} sx={{ fontSize: "16px !important" }} />
          <DtvBox sx={{ fontSize: "16px !important" }}>{pkgName}</DtvBox>
        </DtvBox>
    );
    const themePackageBgColor = {};
    const themePkgTextColors = {};
    if (theme && theme?.packageTiles && Object.keys(theme?.packageTiles).length > 0) {
      Object.keys(theme?.packageTiles).forEach(pkg => {
        themePackageBgColor[pkg] = theme?.packageTiles?.[pkg]?.backgroundColor;
        themePkgTextColors[pkg] = theme?.packageTiles?.[pkg]?.textColor;
      });
    }
    const modalWithNoCTA = modalView && isValidModalSrc(modalSrc);
    const bgColors = themePackageBgColor || props?.packageBgColor || defaultBgColors;
    const pkgTextColors = themePkgTextColors || packageTextColorMap;
    const commonCategory = {};
    let commonCategoryChannelsArray = [];
    let displayCommonCategory = false;

    /** To Do...
     * -- Pull heavy logic out of nested maps into pre-load function (ideally later to ssr)
     *    - Implement loading skeleton while computing?
     * -- Seperate channel grid row into seperate component, do cleanup
     * -- Cleanup styles, including below design errors
     *    - Remove legacy styles
     *    - Move scrollbar to far right of screen on mobile
     *    - Local/Rsn chips should be slightly covering icons
     *    - Top of the checkmark icons is getting cut off
     *    - Checkmark icons not vertically/horizontally centered
     *    - "Exclusively on" text not horizontally centered under the package name
     */

    const getRsnTeams = channel => {
      const teams = [];
      if(channel?.nba) {
        const nbaTeams = channel.nba?.split(",") || [];
        nbaTeams.forEach(team => {
          teams.push(team.trim());
        })
      }
      if(channel?.mlb) {
        const mlbTeams = channel.mlb?.split(",") || [];
        mlbTeams.forEach(team => {
          teams.push(team.trim());
        })
      }
      if(channel?.nhl) {
        const nhlTeams = channel.nhl?.split(",") || [];
        nhlTeams.forEach(team => {
          teams.push(team.trim());
        })
      }
      if(channel?.mls) {
        const nflTeams = channel.mls?.split(",") || [];
        nflTeams.forEach(team => {
          teams.push(team.trim());
        })
      }
      if(channel?.wnba) {
        const nflTeams = channel.wnba?.split(",") || [];
        nflTeams.forEach(team => {
          teams.push(team.trim());
        })
      }
      return teams.join(", ");
    }

    const getEmbeddedSvodsImg = (channel: any, _chnlIndex: any) => {
      const icons = channel?.chlupModalData?.icon;
      return (
        <Tooltip
            enterTouchDelay={0}
            placement="top"
            PopperProps={{
              sx: {
                marginBottom:
                  "-25px !important",
                width: "auto !important",
                fontSize:
                  "16px !important"
              }
            }}
            title={getTooltipContent(
              channel.name
            )}
          >
            { icons && Object.keys(icons)?.length > 0 && (
              <picture style={{
 width: "100%",
height: "100%"
}}>
                <source media={MBL_SIZE} srcSet={icons?.mobile || icons?.default} />
                <source media={TBL_SIZE} srcSet={icons?.tablet || icons?.default} />
                <img
                  alt={channel?.name} src={icons?.default || icons}
                  style={{
                    fontSize: "8px",
                    position: "relative",
                    width: "64px",
                    height: "64px",
                    objectFit: "contain",
                    margin: "8px 0px"
                  }}
                  onError={e => {
                    console.error("[PackagesGrid][EmbeddedSvodRow] untracked icon error", e);
                  }}
                  />
              </picture>
            )}
        </Tooltip>)
    }
    // packageBgColor depends only on packageItem/bgColors/pkgTextColors —
    // invariant across embeddedSvodsData rows — compute once instead of once per row.
    const embeddedSvodPkgBgColors = props?.packages?.map(packageItem => {
      const packageName = packageItem?.tempName || packageItem?.name;
      return getPackageColors(packageName, bgColors, pkgTextColors)?.packageBgColor;
    }) || [];
    const getEmbeddedSvods = () => (
      embeddedSvodsData?.map((channel, subIndex) => {
        const deviceIncludedText = channel?.chlupModalData?.deviceIncludedText;
        const checkDeviceIncludedText = deviceIncludedText !== undefined && deviceIncludedText !== '';
        const packageListSystem = channel?.packageListSystem;
        return (
            <DtvBox
              id={`EmbeddedSvodRow-${channel?.name}`}
              key={`${channel?.offerCode || channel?.name}-${subIndex}`}
              sx={{
                display: "flex",
                width: "100%",
                margin: "0px !important",
                padding: "0px",
                background: "transparent",
                border: "none",
                borderBottom: `1px solid ${theme && theme?.grid?.border ? theme?.grid?.border : "#D1D1D1"}`,
                flex: "1 1 auto" // Allow the div to grow and fill available space
              }}
            >
              <DtvBox
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  border: "none",
                  p: "0px",
                  height: "min-content",
                  ...channelColWidthStyle,
                  lineHeight: "0px !important",
                  flex: "1 1 auto" // Allow the child div to grow and fill available space
                }}
              >
                <DtvBox
                  sx={{
                    backgroundColor: "transparent",
                    padding: "0px",
                    margin: "0px"
                  }}
                >
                  {getEmbeddedSvodsImg(channel, subIndex)}
                </DtvBox>
              </DtvBox>
              <DtvBox
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: "1 1 auto", // Allow the div to grow and fill available space
                  ...embeddedSvodColWidthStyle
                }}
              >
                <DtvBox
                  sx={{
                    display: "flex",
                    width: "100%",
                    height: "100%",
                    margin: "0px !important",
                    padding: "0px",
                    background: "transparent",
                    border: "none"
                  }}
                >
                  {props?.packages?.map((packageItem, pkgIndex) => {
                    const packageBgColor = embeddedSvodPkgBgColors[pkgIndex];
                     const tableBodyCatCols2 =
                     pkgIndex === cmprPkgIndex ?
                       { backgroundColor: packageBgColor } :
                       {};
                    return (
                      <DtvBox
                        id={`${channel?.name}-${pkgIndex}`}
                        key={`${channel?.name}-icon-${pkgIndex}`}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: {
                            xs: "2px",
                            sm: "8px"
                          },
                          flexBasis: `calc(100% / ${props?.packages?.length})`,
                          width: `calc(100% / ${props?.packages?.length})`,
                          borderLeft: `1px solid ${theme && theme?.grid?.border ? theme?.grid?.border : "#D1D1D1"}`, //
                          borderBottom: "none",
                          textAlign: "center",
                          ...tableBodyCatCols2
                        }}
                      >
                        {packageListSystem &&
                          packageListSystem?.length > 0 &&
                          packageListSystem?.includes(packageItem?.id) && (
                            <Tooltip
                              enterTouchDelay={0}
                              placement="top"
                              PopperProps={{
                                sx: {
                                  marginBottom: "-6px !important",
                                  width: "auto !important",
                                  fontSize: "16px !important"
                                }
                              }}
                              title={getTooltipContent(
                                `${channel?.name} in`,
                                `${packageItem?.name}`
                              )}
                            >
                              <DtvBox component="span">
                                <CardMedia
                                  alt={`Included in ${packageItem?.name}`}
                                  component="img"
                                  src={
                                    theme && theme?.grid?.mode === "dark" ? "/assets/content/4o3fwhj9oa/svg/included-onDark.svg" : "/assets/content/enyud976rg/svg/included-onLight.svg"
                                  }
                                  sx={{
                                    ...styles.icons12
                                  }}
                                />
                              </DtvBox>
                            </Tooltip>
                          )}
                      </DtvBox>
                    );
                  })}
                </DtvBox>
                {checkDeviceIncludedText && (
                  <DtvText
                    content={deviceIncludedText}
                    variant="body3"
                    sx={{
                      padding: "4px 20px 4px 20px",
                      border: `1px solid ${theme && theme?.grid?.border ? theme?.grid?.border : '#D1D1D1'}`,
                      backgroundColor: theme && theme?.grid?.highLightRowBackground ? theme?.grid?.highLightRowBackground : "#F4F4F4",
                      color: theme && theme?.grid?.highLightRowTextColor ? `${theme?.grid?.highLightRowTextColor} !important` : "#102641 !important",
                      textAlign: "center"
                    }}
                  />
                )}
              </DtvBox>
            </DtvBox>
        );
      })
    )
    return (
      <Table id={id}>
        <TableHead
          id="channels-table-head"
          ref={ref => {
            if (ref) {
              tableHeaderRef = ref;
            }
          }}
          sx={{
            ...stickyHeaderStyles,
            backgroundColor: theme && theme?.grid?.background ? `${theme?.grid?.background} !important` : colorPalette.neutral_white.main
          }}
        >
          <DtvGrid
            container
            component={TableRow}
            sx={{
              ...styles.tablerowContainer,
              ...styles.maxWidth,
              ...styles.tableBorder,
              backgroundColor: isChannelLineUpPage ?
                colorPalette.jet.main :
                theme && theme?.grid?.background ? theme?.grid?.background : "#eeeeee",
              alignItems: { md: "flex-end" },
              color: theme && theme?.grid?.textColor
            }}
          >
            <DtvGrid
              item
              alignItems="center"
              component={TableCell}
              data-testId={'tableCell-channel-heading'}
              id="tableCell-head"
              justifyContent="center" key="header"
              sx={{
                flexDirection: verticalPkgNamesOn ? "column" : "row",
                backgroundColor: isChannelLineUpPage ? "#102641" : theme && theme?.grid?.background ? theme?.grid?.background : "transparent",
                border: "none",
                padding: "0px",
                fontSize: "14px",
                ...(isMobileVP && {
                  paddingBottom: "4px !important"
                }),
                ...channelColWidthStyle,
                color: theme && theme?.grid?.textColor
              }}
            >
              <Paper
                square
                elevation={0}
                id="paper"
                sx={{
 ...styles.paper,
color: theme && theme?.grid?.textColor
}}>
                <DtvGrid
                  container
                  alignItems="center"
                  id="chnls" justifyContent="center"
                >
                  {isChannelLineUpPage === false ? <></> : (
                    <DtvGrid sx={{ maxWidth: "139px" }}>
                      <DtvText
                        component="p"
                        content="CHANNELS"
                        variant="body3"
                        sx={{
                          textAlign: "left",
                          mat: {
 xs: "33.5px",
sm: 0
},
                          fontSize: {
 xs: "20px",
sm: "20px",
md: "24px"
},
                          color: theme && theme?.grid?.textColor
                        }}
                      />
                      <DtvText
                        component="p"
                        content="Choose an option below to switch channel arrangement"
                        variant="body3"
                        sx={{
                          textAlign: "left",
                          margin: "3px 0px",
                          color: theme && theme?.grid?.textColor
                        }}
                      />
                      <DtvBox
                        sx={{
                          ...(!isDesktopVP && {
                            "& label span:first-child": {
                              height: { xs: "24px !important" },
                              width: { xs: "24px !important" }
                            }
                          })
                        }}
                      >
                        <RadioButton
                          {...primaryDtvRadioButton}
                          onChange={e => {
                            const option = e.target.value;
                            option === "0" ? setDefaultSortOption(true) : setDefaultSortOption(false);
                            props?.changeViewModeFromNationalTable(parseInt(option));
                          }}
                        />
                      </DtvBox>
                    </DtvGrid>
                  )}
                </DtvGrid>
              </Paper>
            </DtvGrid>
            {/* note: edsp=false query param for spanish*/}
            {packages && packages.map((packageObj, pkgInd) => {
              try {
                const {
                  googleAnalytics: _googleAnalytics,
                  packageConfig
                } = packageObj;
                // eslint-disable-next-line no-constant-binary-expression
                const channelCount = (`${(packageConfig?.channel?.displayCount || "") + (packageConfig?.channel?.signLetter || packageObj?.channelCount || "")  } ${  packageConfig?.channel?.label || ""}`) || "";
                const packageName =
                  packageObj?.tempName || packageObj?.name;
                const { packageBgColor, packageTextColor } =
                  getPackageColors(packageName, bgColors, pkgTextColors);
                const { displayPkgCta, modalCTA } =
                  getPackageCta(packageObj, modalSrc, modalView, modalWithNoCTA, showCta, showCtaCI);
                const analyticsCTALinkDestination =
                  modalCTA?.props?.href?.includes(".directv.com") ?
                    modalCTA?.props?.href :
                    `${window.location.origin}${modalCTA?.props?.href}`;

                const isLastCol = pkgInd === packages.length - 1;
                const isCmprPkgIndex = pkgInd === cmprPkgIndex;
                return (
                  <DtvGrid
                    container
                    alignItems="center"
                    className="package-container"
                    component={TableCell}
                    data-testId={`tableCell-channel-heading-${packageName}`}
                    justifyContent="center"
                    key={pkgInd}
                    sx={{
                      backgroundColor: isChannelLineUpPage ?
                        colorPalette.jet.main :
                        theme && theme?.grid?.background ? theme?.grid?.background : colorPalette.neutral_white.main,
                      borderRadius: "8px 8px 0px 0px",
                      border: "none",
                      padding: "0px",
                      ...isLastCol && isChannelLineUpPage && isMobileVP && {
                        paddingRight: "16px"
                      },
                      ...mainColWidthStyle,
                      ...isChannelLineUpPage && isMobileVP && {
                        maxWidth: "17.5% !important"
                      }
                    }}
                  >
                    <Paper
                      square
                      elevation={1}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        position: "relative",
                        backgroundColor: packageObj?.pkgBgcolor ?
                          packageObj?.pkgBgcolor :
                          packageBgColor,
                        borderRadius: "8px 8px 0px 0px",
                        height: "100%",
                        width: "100%",
                        margin: "0px",
                        ...(verticalPkgNamesOn ? {
                          alignItems: "center",
                          padding:
                            isMobileVP ? "12px 1px 9px 1px" :
                              isTabletVP ? "16px 6px" :
                                "16px 8px"
                        } : {
                          alignItems: "center",
                          padding: !isSinglePkgUseCase ? (isTabletVP ? "20px 12px" : "20px 15px") : (
                            isMobileVP ? "18px 3px 8px 3px" :
                              "20px 13px"
                          )
                        }),
                        ...(!isLastCol ?
                          {
 width: "calc(100% - 2px)",
margin: "0px 1px"
} :
                          {
 width: "calc(100% - 1px)",
margin: "0px 0px 0px 1px"
}
                        )
                      }}
                    >
                      {isCmprPkgIndex && <DtvBox sx={{
                        position: "absolute",
                        width: "100%",
                        height: "12px",
                        top: 0,
left: 0,
                        borderRadius: "8px 8px 0px 0px",
                        backgroundColor: theme && theme?.selectedPkgBgColor ? theme?.selectedPkgBgColor : `${colorPalette?.blue?.[600]}`  // packageColors[`headerBgStrip${packageColorMap[cmprPkg]}Selected`]
                      }} />}
                      <DtvGrid
                        container
                        className="package-text-container"
                        sx={{
                          ...styles.headerPackageFontStyles,
                          width: 'fit-content',
                          height: 'fit-content',
                          display: "block",
                          ...(verticalPkgNamesOn && {
                            flexDirection: "column !important",
                            writingMode: "vertical-rl ",
                            textOrientation: "mixed",
                            transform: "rotate(180deg)",
                            margin: "auto",
                            height: maxPkgNameHeight,
                            marginTop: "8px"
                          })
                        }}>
                        <DtvBox sx={{ ...(verticalPkgNamesOn ? { height: "100%" } : { width: "100%" }) }}>
                          <DtvText
                            className="package-name"
                            content={(packageConfig?.richTextDisplayLabel || packageConfig?.displayLabel || packageName)?.replace?.('™', '<sup> TM</sup>')}
                            sx={{
                              textAlign: "left",
                              fontSize: !isDesktopVP ? "18px !important" : "20px !important",
                              fontWeight: "600 !important",
                              color: packageTextColor,
                              "& sup": {
                                fontSize: "10px",
                                lineHeight: 0
                              },
                              ...(verticalPkgNamesOn ?
                                { height: "fit-content" } :
                                { width: "100%" })
                            }} />
                        </DtvBox>
                        <DtvBox sx={{ ...(verticalPkgNamesOn ? { height: "100%" } : { width: "100%" }) }}>
                          <DtvText
                            className="package-chnl-count"
                            content={channelCount}
                            sx={{
                              whiteSpace: "nowrap",
                              fontSize: "14px !important",
                              textAlign: "left",
                              color: packageTextColor,
                              lineHeight: "18.2px",
                              fontWeight: "500 !important",
                              ...(verticalPkgNamesOn ?
                                { height: "fit-content" } :
                                { width: "100%" })
                            }}
                          />
                        </DtvBox>
                      </DtvGrid>

                      {displayPkgCta && (
                        <DtvGrid container
                          sx={{
                            // textAlign: "center",
                            padding: "0px",
                            width: "100%",
                            justifyContent: "center",
                            margin: isMobileVP ? "10px 0px 0px 0px" : isTabletVP ? "12px 0px 0px 0px" : "16px 0px 0px 0px"
                          }}
                        >
                          <DtvButton
                            color="primary"
                            data-testId="shopButton123"
                            googleAnalyticsData={getAnalyticsAttrs((isMobileVP && modalCTA?.props?.text?.includes('Shop')) ? 'shop' : modalCTA?.props?.text?.toLowerCase?.(), 'modal_channel lineup', analyticsCTALinkDestination)}
                            mode={theme && theme?.grid?.mode === "dark" ? "light" : "dark"}
                            target="_parent"
                            targeturl={modalCTA?.props?.href}
                            variant="uxprimary"
                            aria-describedby={(isMobileVP && modalCTA?.props.text.includes('Shop')) ? "Shop link" :
                              `${modalCTA?.props.text  }link`}
                            ariaLabel={(isMobileVP && modalCTA?.props.text.includes('Shop')) ? "Shop link" :
                              `${modalCTA?.props.text  }link`}
                            label={(isMobileVP && modalCTA?.props.text.includes('Shop')) ? "Shop" :
                              modalCTA?.props.text}
                            {...modalCTA}
                            sx={{
                              fontFamily: "roboto-medium",
                              fontWeight: 500,
                              lineHeight: "0",
                              textTransform: "inherit",
                              textWrap: "nowrap",
                              fontSize: "14px",
                              borderRadius: "100px",
                              width: "100%",
                              maxWidth: "100%",
                              height: "26px !important",
                              ...(pkgInd === cmprPkgIndex && modalSrc === "SALES" && {
                                display: 'none'
                              }),
                              ...isMobileVP && {
                                padding: "15px 15px",
                                margin: "0px 4px"
                              }
                            }}
                          />
                        </DtvGrid>
                      )}
                    </Paper>
                  </DtvGrid>
                );
              } catch (error) {
                console.log("Error in package loop: ", error);
              }
              return null;
            })}
          </DtvGrid>
        </TableHead>
        <TableBody id="tableBody">
          {(props?.viewMode === CHANNEL_LINEUP_VIEW.CHANNELS ||
            props?.viewMode === CHANNEL_LINEUP_VIEW.CHANNELNUMBER ||
            (props?.viewMode === CHANNEL_LINEUP_VIEW.CATEGORY && isSearchActive)) &&
            (props?.channels?.length > 0 || (embeddedSvodsData && embeddedSvodsData.length > 0)) &&
            chnlNameNumberCategories &&
            chnlNameNumberCategories.map((category, index) => {
              const isCommonCategory = category.key === "ADD_ON_CHANNELS";
              // eslint-disable-next-line camelcase
              const nct_uniq = uniqId();
              const myFreeDtvFlag = category.key === "MYFREEDTV_CHANNELS";
              // Computed once here — reused for both the accordion height fix
              // (below) and the subheading render gate, instead of re-deriving
              // category.key === "MYFREEDTV_CHANNELS" && isSatelliteQP at each site.
              const showMyFreeSubheading = myFreeDtvFlag && isSatelliteQP && Boolean(commonData?.myFreeDtvSubheading);
              let categoryChannels = props?.channels?.filter(channel => {
                if (myFreeDtvFlag) {
                  return channel?.isMyFreeDTV === myFreeDtvFlag
                }
                  return channel?.isMyFreeDTV === undefined || channel?.isMyFreeDTV === myFreeDtvFlag
              });
              if (customPkgIndex) {
                categoryChannels = channels.filter(channel => {
                  const packageIndicators =
                    (channel &&
                      channel?.packageIndicators &&
                      channel?.packageIndicators[
                      `${props?.edspFlow ? "edsp" : "2yr"}`
                      // eslint-disable-next-line camelcase
                      ][pkgCtgySelected_upc]) ||
                    [];
                  return (
                    packageIndicators[customPkgIndex] ===
                    PACKAGE_INDICATORS.IN ||
                    channel.category === "LOCAL" ||
                    channel.category === "RSN"
                  );
                });
              }
              const addOnChnls = channels.filter(channel => {
                const packageIndicators =
                  (channel &&
                    channel?.packageIndicators &&
                    channel?.packageIndicators[
                    `${props?.edspFlow ? "edsp" : "2yr"}`
                    // eslint-disable-next-line camelcase
                    ][pkgCtgySelected_upc]) ||
                  [];
                const addOnPkgIndicators =
                  (channel &&
                    channel?.addOnPkgIndicators &&
                    channel?.addOnPkgIndicators[
                    `${props?.edspFlow ? "edsp" : "2yr"}`
                    // eslint-disable-next-line camelcase
                    ][pkgCtgySelected_upc]) ||
                  [];
                const isAddOn =
                  addOnPkgIndicators.some(
                    x => x === PACKAGE_INDICATORS.ADDON || x === PACKAGE_INDICATORS.PROMO
                  );
                return (
                  (isAddOn ||
                    packageIndicators.includes(PACKAGE_INDICATORS.ADDON) ||
                    packageIndicators.includes(PACKAGE_INDICATORS.PROMO)) && (channel?.isMyFreeDTV === undefined || channel?.isMyFreeDTV === false)
                );
              });
              if (!displayCommonCategory) {
                displayCommonCategory = addOnChnls.length > 0;
              }
              if (isCommonCategory || categoryChannels.length > 0 || category.key === "INCLUDED_SUBSCRIPTIONS") {
                if (customPkgIndex !== null) {
                  categoryChannels = categoryChannels.filter(channel => {
                    const isIncludedInAnyPkg =
                      (props?.packages?.some(
                        // WAT-11878: read the pkg's inclusion from its OWN language slot
                        // (see getPkgIndicator helper) so a cmprPkg-injected column
                        // doesn't force channels to appear based on the current view's
                        // package check-marks.
                        pkg => getPkgIndicator(channel, pkg, !!props?.edspFlow, pkgCtgySelected_upc) === PACKAGE_INDICATORS.IN
                      ) ?? false);
                    return (
                      isIncludedInAnyPkg ||
                      channel.category === "LOCAL" ||
                      channel.category === "RSN"
                    );
                  });
                } else {
                  categoryChannels = categoryChannels.filter(channel => {
                    const isIncludedInAnyPkg =
                      (props?.packages?.some(
                        // WAT-11878: read the pkg's inclusion from its OWN language slot
                        // (see getPkgIndicator helper) so a cmprPkg-injected column
                        // doesn't force channels to appear based on the current view's
                        // package check-marks.
                        pkg => getPkgIndicator(channel, pkg, !!props?.edspFlow, pkgCtgySelected_upc) === PACKAGE_INDICATORS.IN
                      ) ?? false);
                    return (
                      isIncludedInAnyPkg ||
                      channel.category === "LOCAL" ||
                      channel.category === "RSN"
                    );
                  });
                }

                const notCommonCategoryChnlsCount = categoryChannels
                  .map(channel => {
                    // eslint-disable-next-line camelcase
                    const channelsQP = channelsQP_lwc || "";
                    let showChannel = true;
                    if (
                      channel?.category === "LOCAL" ||
                      channel?.category === "RSN"
                    ) {
 return channel;
}
                    if (channelsQP !== "") {
                      showChannel = false;
                      const packageIndicators =
                        (channel &&
                          channel.packageIndicators &&
                          channel.packageIndicators[
                          `${edspFlow ? "edsp" : "2yr"}`
                          // eslint-disable-next-line camelcase
                          ][pkgCtgySelected_upc]) ||
                        [];
                      props?.packages?.forEach(
                        (packageItem, pkgIndex) => {
                          const { packageIndicatorIndex } = packageItem;
                          const _packageIndicator = packageIndicators[packageIndicatorIndex];
                          const _iconsInd = pkgIndex;
                          // const packageId = props?.packages[0]?.id?.toUpperCase();
                          const packageId = packageItem?.id
                          let billingCodeLabel = "";
                          if (!isChannelLineUpPage) {
                            if (packageId?.includes("ATT_TV_")) {
                              // eslint-disable-next-line prefer-destructuring
                              billingCodeLabel = packageId.split("ATT_TV_")[1];
                            } else if (packageId?.includes("DTV_")) {
                              // eslint-disable-next-line prefer-destructuring
                              billingCodeLabel = packageId.split("DTV_")[1];
                            }
                            if (
                              (channelsQP === "internet" &&
                                channel?.includedChnlPackages?.includes(
                                  `ATT_TV_${billingCodeLabel}`
                                )) ||
                              (channelsQP === "satellite" &&
                                channel?.includedChnlPackages?.includes(
                                  `DTV_${billingCodeLabel}`
                                ))
                            ) {
                              showChannel = true;
                            }
                          } else {
                            if (packageId?.includes("ATT_TV_")) {
                              // eslint-disable-next-line prefer-destructuring
                              billingCodeLabel = packageId.split("ATT_TV_")[1];
                            }
                            if (
                              channelsQP === "internet" &&
                              channel?.includedChnlPackages?.includes(
                                `ATT_TV_${billingCodeLabel}`
                              )
                            ) {
                              showChannel = true;
                            }
                          }
                        }
);
                    }
                    return showChannel ? channel : false;
                  })
                  .filter(x => x);
                const commonCategoryChnlsCount = addOnChnls
                  .map(channel => {
                    // eslint-disable-next-line camelcase
                    const channelsQP = channelsQP_lwc || "";
                    let showChannel = true;
                    if (channelsQP !== "") {
                      // NOTE: Considering the any channel which is addon or promo for any package will also be addon or promo for Entertainment
                      const entertainmentPkg = props?.packages?.find(p => DEFAULT_ADDON_PROMO_PACKAGES.includes(p.id?.toUpperCase()));
                      const packageId = entertainmentPkg ? entertainmentPkg?.id?.toUpperCase() : props?.packages[0]?.id?.toUpperCase();

                      let billingCodeLabel = "";
                      if (!isChannelLineUpPage) {
                        if (packageId?.includes("ATT_TV_")) {
                          // eslint-disable-next-line prefer-destructuring
                          billingCodeLabel = packageId.split("ATT_TV_")[1];
                        } else if (packageId?.includes("DTV_")) {
                          // eslint-disable-next-line prefer-destructuring
                          billingCodeLabel = packageId.split("DTV_")[1];
                        }
                        if (
                          (channelsQP === "internet" &&
                            !channel?.addonPromoChnlPackages?.includes(
                              `ATT_TV_${billingCodeLabel}`
                            )) ||
                          (channelsQP === "satellite" &&
                            !channel?.addonPromoChnlPackages?.includes(
                              `DTV_${billingCodeLabel}`
                            ))
                        ) {
                          showChannel = false;
                        }
                      } else {
                        if (packageId?.includes("ATT_TV_")) {
                          // eslint-disable-next-line prefer-destructuring
                          billingCodeLabel = packageId.split("ATT_TV_")[1];
                        }
                        if (
                          channelsQP === "internet" &&
                          !channel?.addonPromoChnlPackages?.includes(
                            `ATT_TV_${billingCodeLabel}`
                          )
                        ) {
                          showChannel = false;
                        }
                      }
                    }
                    return showChannel ? channel : false;
                  })
                  .filter(x => x);
                const showAccordion = isCommonCategory ?
                  commonCategoryChnlsCount?.length > 0 :
                  notCommonCategoryChnlsCount?.length > 0 || embeddedSvodsData?.length > 0;
                return (
                  showAccordion && (
                    <TableRow
                      id="tableBodyRow"
                      key={index}
                      sx={{
                        backgroundColor: isChannelLineUpPage ?
                          "#102641" :
                          theme && theme?.background ? theme?.background : "#FFFF",
                        [breakpoints.between(
                          breakpoints.values.xs,
                          breakpoints.values.xl
                        )]: {
                          fontSize: "14px",
                          padding: "0px !important"
                        }
                      }}
                    >
                      <TableCell
                        colSpan={5}
                        sx={{
                          [breakpoints.between(
                            breakpoints.values.xs,
                            breakpoints.values.xl
                          )]: {
                            fontSize: "14px",
                            padding: "0px !important"
                          },
                          "@media (min-width: 1700px)": {
                            padding: "0px !important"
                          }
                        }}
                      >
                        <Accordion
                          TransitionProps={{ unmountOnExit: true }}
                          expanded={chnlNameNumberAccordion?.[index]?.expanded}
                          id="accordion"
                          key={index}
                          sx={{
                            ...styles.categoryAccordion,
                            marginTop: isChannelLineUpPage ?
                              "10px !important" :
                              0
                          }}
                          onClick={event => handleChnlNameNumberClick(event, index)
                          }
                        >
                          {(categoryChannels?.length > 0 ||
                            (category.key === "ADD_ON_CHANNELS" &&
                              addOnChnls?.length > 0) || (embeddedSvodsData?.length > 0 && category.key === "INCLUDED_SUBSCRIPTIONS")) && (
                              <AccordionSummary
                                expandIcon={
                                  <CardMedia
                                    alt="Click to open dropdown"
                                    component="img"
                                    id="close-arrow"
                                    src={theme && theme?.grid?.mode === "dark" ? "https://www.directv.com/dtvassets/global/icons/2022/arrow-down_dk-bg.svg" : "https://www.directv.com/dtvassets/global/icons/fusion/arrow_jet_down.svg"}
                                    sx={{ ...styles.dropDownIcon }}
                                  />
                                }
                                // aria-controls={`nct-panel${index}-content-${nct_uniq}`}
                                aria-controls={`accordion`}
                                // eslint-disable-next-line camelcase
                                id={`nct-panel${index}-header-${nct_uniq}`}
                                sx={{
                                  ...styles.accSummaryColor,
                                  ...styles.accordionSummary,
                                  minHeight: "45px !important",
                                  height: "45px !important",
                                  ...((isCommonCategory || showMyFreeSubheading) &&
                                    chnlNameNumberAccordion?.[index]?.expanded && {
                                      minHeight: "100%",
                                      height: "100%"
                                    }),
                                  position: "relative",
                                  "& .MuiAccordionSummary-content": {
                                    margin: {
                                      md: "12px 0  !important",
                                      sm: "9px 0 !important",
                                      xs: "9px 0 !important"
                                    }
                                  },
                                  // With the MyFree caveat band showing, the content's own
                                  // 12px vertical margin stacks on top of the summary's 12px
                                  // padding, so the label sat 24px down from the header edge
                                  // (and the band 24px up from the bottom). Zero the margin
                                  // and let the padding alone own the 12px gap. !important
                                  // because the rule above (and styles.accordionSummary) set
                                  // that margin with !important.
                                  ...(showMyFreeSubheading &&
                                    chnlNameNumberAccordion?.[index]?.expanded && {
                                      "& .MuiAccordionSummary-content": {
                                        margin: "0 !important"
                                      }
                                    }),
                                  ...styles.expandIconPosition,
                                  ...(latestAccordionOpen.includes(index) && {
                                    ...stickyAccordionStyles
                                  })
                                }}
                              >
                                <DtvBox
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    width: "100%"
                                  }}
                                >
                                  <DtvBox sx={{ width: "100%" }}>
                                    <Typography
                                      id={`${index}-catergoryValue`}
                                      key={`${index}-catergoryValue`}
                                      sx={{
                                          ...styles.typography,
                                          color: theme && theme?.sectionHeaders?.textColor ? theme?.sectionHeaders?.textColor : "#102641"
                                      }}
                                    >
                                      {category.key === "ADD_ON_CHANNELS" ?
                                        "Add-on channels" :
                                        category.value}
                                    </Typography>
                                  </DtvBox>
                                  {showMyFreeSubheading && chnlNameNumberAccordion?.[index]?.expanded && (
                                    <MyFreeDtvSubheading
                                      content={commonData?.myFreeDtvSubheading}
                                      id={`myfreeDtv-subheading-container-nct-${index}`}
                                      theme={theme}
                                    />
                                  )}
                                  <DtvBox sx={{ width: "100%" }}>
                                    {isCommonCategory &&
                                      chnlNameNumberAccordion?.[index]?.expanded && (
                                        <>
                                          <DtvBox
                                            id="addonPromo-info-sec"
                                            sx={
                                              isSticky &&
                                                category.key === "ADD_ON_CHANNELS" ?
                                                {
                                                  ...styles.infoSec,
                                                  marginBottom:
                                                    "8px !important"
                                                } :
                                                {
                                                  ...styles.infoSec,
                                                  marginBottom:
                                                    "8px !important"
                                                }
                                            }
                                          >
                                            <DtvBox
                                              id="included-1"
                                              sx={{
                                                padding: "3px 0px 3px 0px",
                                                ...styles.addOnDiv,
                                                [breakpoints.between(
                                                  breakpoints.values.xs,
                                                  breakpoints.values.xl
                                                )]: {
                                                  fontSize: "14px",
                                                  borderBottom: "0px !important",
                                                  display: "flex",
                                                  alignItems: "center"
                                                }
                                              }}
                                            >
                                              <CardMedia
                                                alt={"Included channels"}
                                                component="img"
                                                sx={{ ...styles.legendIcons }}
                                                src={
                                                  theme && theme?.grid?.mode === "dark" ? "/assets/content/4o3fwhj9oa/svg/included-onDark.svg" : "/assets/content/enyud976rg/svg/included-onLight.svg"
                                                }
                                              />
                                              <DtvText
                                                variant="smalltext"
                                                content={
                                                  commonData?.includedChnlLogoInfo ||
                                                  "Channel is included in package"
                                                }
                                                sx={{
 ...styles?.addOnPromoTextDiv,
color: theme && theme?.sectionHeaders?.textColor
}}
                                              />
                                            </DtvBox>
                                            <DtvBox
                                              id="add-on"
                                              sx={{
                                                padding: "3px 0px 3px 0px",
                                                ...styles.addOnDiv,
                                                [breakpoints.between(
                                                  breakpoints.values.xs,
                                                  breakpoints.values.xl
                                                )]: {
                                                  fontSize: "14px",
                                                  borderBottom: "0px !important",
                                                  display: "flex",
                                                  alignItems: "center"
                                                }
                                              }}
                                            >
                                              <CardMedia
                                                alt={"Add on channels"}
                                                component="img"
                                                sx={{ ...styles.legendIcons }}
                                                src={
                                                  theme && theme?.grid?.mode === "dark" ? "/assets/content/dvwlcmuqjk/svg/addon-onDark.svg" : "/assets/content/pr32m1cdgf/svg/addon-onLight.svg"
                                                }
                                              />
                                              <DtvText
                                                variant="smalltext"
                                                content={
                                                  commonData?.addOnChnlLogoInfo ||
                                                  "Add-on channel (Channel can be added for an additional cost)"
                                                }
                                                sx={{
 ...styles?.addOnPromoTextDiv,
color: theme && theme?.sectionHeaders?.textColor
}}
                                              />
                                            </DtvBox>
                                            <DtvBox
                                              id="promo"
                                              sx={{
                                                padding: "3px 0px 0px 0px",
                                                ...styles.promoDiv,
                                                [breakpoints.between(
                                                  breakpoints.values.xs,
                                                  breakpoints.values.xl
                                                )]: {
                                                  fontSize: "14px",
                                                  borderBottom: "0px !important",
                                                  display: "flex",
                                                  alignItems: "center"
                                                }
                                              }}
                                            >
                                              <CardMedia
                                                alt={"Promo Available"}
                                                component="img"
                                                sx={{ ...styles.legendIcons }}
                                                src={
                                                  theme && theme?.grid?.mode === "dark" ? "/assets/content/hni5xiiqjh/svg/promo-onDark.svg" : "/assets/content/joqxso5l7u/svg/promo-onLight.svg"
                                                }
                                              />
                                              <DtvText
                                                variant="smalltext"
                                                content={
                                                  commonData?.promoChnlLogoInfo ||
                                                  "Promotion (Channel is included for an introductory time)"
                                                }
                                                sx={{
 ...styles?.addOnPromoTextDiv,
color: theme && theme?.sectionHeaders?.textColor
}}
                                              />
                                            </DtvBox>
                                          </DtvBox>
                                        </>
                                      )}
                                  </DtvBox>
                                </DtvBox>
                              </AccordionSummary>
                            )}

                          <AccordionDetails
                            key={`${index}-accordionDetails`}
                            sx={{ ...styles.accordionDetails }}
                          >
                            <Typography
                              key={`${index}-catergory`}
                              sx={{
                                ...styles.typography,
                                color: "#333333",
                                width: "100%"
                              }}
                            >
                              <Table id="nestedTable">
                                <TableBody
                                  id="nestedTableBody"
                                  style={{ backgroundColor: theme && theme?.grid?.background ? theme?.grid?.background : "#ffffff" }}
                                >
                                  {(category.key === "INCLUDED_SUBSCRIPTIONS" ?
                                    getEmbeddedSvods()                                  :
                                  <>
                                  {!isCommonCategory &&
                                    categoryChannels.map(
                                      // eslint-disable-next-line complexity
                                      (channel, channelIndex) => {
                                        const firstRow = channelIndex === 0 ? styles.tablefirstRowCat : styles.tablerowContainer;
                                        // eslint-disable-next-line camelcase
                                        const channelsQP = channelsQP_lwc || "";
                                        const highlightRow =
                                          (channel?.isSatelliteInc &&
                                            !channel?.isStreamInc) ||
                                          (!channel?.isSatelliteInc &&
                                            channel?.isStreamInc);
                                        const rowStyles =
                                          highlightRow && channelsQP === "" ?
                                            styles.borderHighlightAcc :
                                            {};
                                        let showChannel = true;
                                        if (channelsQP !== "") {
                                          showChannel = false;
                                          const _packageIndicators =
                                            // eslint-disable-next-line camelcase
                                            (channel && channel.packageIndicators && channel.packageIndicators[`${edspFlow ? "edsp" : "2yr"}`][pkgCtgySelected_upc]) ||
                                            [];
                                          props?.packages?.forEach(
                                            packageItem => {
                                              const packageId = packageItem?.id?.toUpperCase();
                                              let billingCodeLabel = "";
                                              if (!isChannelLineUpPage) {
                                                if (packageId?.includes("ATT_TV_")) {
                                                  // eslint-disable-next-line prefer-destructuring
                                                  billingCodeLabel =
                                                    packageId.split("ATT_TV_")[1];
                                                } else if (packageId?.includes("DTV_")) {
                                                  // eslint-disable-next-line prefer-destructuring
                                                  billingCodeLabel = packageId.split("DTV_")[1];
                                                }
                                                if (
                                                  (channelsQP === "internet" &&
                                                    channel?.includedChnlPackages?.includes(`ATT_TV_${billingCodeLabel}`)) ||
                                                  (channelsQP === "satellite" &&
                                                    channel?.includedChnlPackages?.includes(`DTV_${billingCodeLabel}`))
                                                ) {
                                                  showChannel = true;
                                                }
                                              } else {
                                                if (
                                                  packageId?.includes("ATT_TV_")
                                                ) {
                                                  // eslint-disable-next-line prefer-destructuring
                                                  billingCodeLabel =
                                                    packageId.split(
                                                      "ATT_TV_"
                                                    )[1];
                                                }
                                                if (
                                                  channelsQP === "internet" &&
                                                  channel?.includedChnlPackages?.includes(
                                                    `ATT_TV_${billingCodeLabel}`
                                                  )
                                                ) {
                                                  showChannel = true;
                                                }
                                              }
                                              if (channel?.category === "RSN" && !channel?.packageNames?.find(pkgName => pkgName?.toLowerCase().includes(packageItem?.billingCodeLabel?.toLowerCase()))) {
                                                // Removing for Entertainment pkg. TO-DO: check and read from packageNames returned by API
                                                showChannel = false;
                                              }
                                            }
                                          );
                                          if (channel?.category === "LOCAL" || channel?.category === "RSN") {
                                            showChannel = true;
                                          }
                                        }
                                        const highLightBgRow =
                                          !isChannelLineUpPage && highlightRow && channelsQP === "" ?
                                            styles.highlightRowBgColor :
                                            {};
                                        // const singlePkg = customPkgIndex!=null && props?.packages?.[0]?.name?.toLowerCase();

                                        const accordionChannelRowElement = (
                                          <>
                                            <DtvGrid
                                              container
                                              component={TableRow}
                                              id={`row-${channel.chnlNum}-${channelIndex}`}
                                              key={channelIndex}
                                              sx={{
                                                width: "100%",
                                                backgroundColor: isChannelLineUpPage ? "#102641" : theme && theme?.grid?.background ? theme?.grid?.background : colorPalette.neutral_white.main,
                                                ...styles.tableBorder,
                                                ...styles.accordionRow,
                                                ...(channelIndex === categoryChannels.length - 1 ?
                                                  {
                                                    firstRow,
                                                    ...styles.marginRow,
                                                    ...rowStyles,
                                                    ...styles.mb
                                                  } :
                                                  {
                                                    firstRow,
                                                    ...styles.marginRow,
                                                    ...rowStyles
                                                  })
                                              }}
                                            >
                                              {!isChannelLineUpPage && highlightRow && channelsQP === "" && (
                                                <DtvBox
                                                  component={TableCell}
                                                  id="tableCell"
                                                  sx={{
                                                    padding: "0px !important",
                                                    width: "100%",
                                                    backgroundColor: theme && theme?.grid?.highLightRowBackground ? theme?.grid?.highLightRowBackground : '#D1D1D1',
                                                    color: theme && theme?.grid?.highLightRowTextColor ? `${theme?.grid?.highLightRowTextColor} !important` : "#000000",
                                                    borderBottom: `${isChannelLineUpPage ? "1" : "2"
                                                      }px solid ${isChannelLineUpPage ? "#D1D1D1" : theme && theme?.grid?.border ? theme?.grid?.border : '#D1D1D1' }`,
                                                    position: "relative",
                                                    textAlign: "center",
                                                    height: {
                                                      xs: "18px",
                                                      sm: "22px"
                                                    },
                                                    lineHeight: {
                                                      xs: "1.3em",
                                                      sm: "1.7em"
                                                    },
                                                    top: channelIndex === 0 && "8px",
                                                    [breakpoints.between(breakpoints.values.xs, breakpoints.values.xl)]: {
                                                      fontSize: "14px"
                                                    },
                                                    "@media (min-width: 1700px)": {
                                                      height: "32px"
                                                    }
                                                  }}
                                                >
                                                  {`Exclusively on ${channel?.isSatelliteInc ?
                                                    "Satellite. See below for package details" :
                                                    "Internet"
                                                    }`}
                                                </DtvBox>
                                              )}
                                              <DtvGrid
                                                item
                                                alignItems="center"
                                                component={TableCell}
                                                id="tableCell"
                                                justifyContent="flex-start"
                                                key={`logo${channelIndex}`}
                                                sx={{
                                                  ...styles.tableColPad,
                                                  ...styles.borderRow,
                                                  ...styles.paper,
                                                  ...highLightBgRow,
                                                  // ...styles.tableBodyRowHeight,
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  justifyContent: "flex-start",
                                                  alignItems: "center",
                                                  padding: "4px",
                                                  position: "relative",
                                                  ...channelColWidthStyle,
                                                  ...(detailsVisible &&
                                                    selectedChannel === channel.chnlNum && {
                                                    borderBottom: "none !important"
                                                  }),

                                                  [breakpoints.down(
                                                    breakpoints.values
                                                      .sm
                                                  )]: {
                                                    minWidth: isChannelLineUpPage && {
                                                      xs: "32% !important"
                                                    }
                                                  }
                                                }}
                                              >
                                                <Tooltip
                                                  enterTouchDelay={0}
                                                  placement="top"
                                                  PopperProps={{
                                                    sx: {
                                                      marginBottom:
                                                        "-25px !important",
                                                      width: "auto !important",
                                                      fontSize:
                                                        "16px !important"
                                                    }
                                                  }}
                                                  title={getTooltipContent(
                                                    channel.name
                                                  )}
                                                >
                                                  <DtvBox component="span">
                                                    {channel.icon ? (
                                                      <>
                                                        <ChannelLogoChip
                                                          alt={channel.name}
                                                          component="span"
                                                          display="inline-flex"
                                                          imgSx={styles.icons}
                                                          margin="6px 0"
                                                          size="64px"
                                                          isOvLogo={channel?.isOvLogo}
                                                          src={channel.icon}
                                                          verticalAlign="middle"
                                                        />
                                                        {(channel?.category ===
                                                          "LOCAL" ||
                                                          channel?.category ===
                                                          "RSN") && (
                                                            <DtvText
                                                              variant="smalltext"
                                                              content={
                                                                channel?.category ===
                                                                  "LOCAL" ?
                                                                  "Local" :
                                                                  "RSN"
                                                              }
                                                              sx={{
                                                                ...styles.channelName,
                                                                color: "#FFF",
                                                                maxWidth: "55px",
                                                                maxHeight: "80px",
                                                                display: "block",
                                                                backgroundColor:
                                                                  "#555555",
                                                                borderRadius:
                                                                  "4px",
                                                                textAlign:
                                                                  "center",
                                                                m: "-4px auto 7px auto",
                                                                p: "5px",
                                                                fontSize: "12px"
                                                              }}
                                                            // tooltip={{
                                                            //   content: channel?.category === "LOCAL"  ? "Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them." : "Regional Sports Networks aren't available everywhere and may vary by package and location. You may need to be in your billing region (ZIP Code) to watch them.",
                                                            //   color: "#FFF",
                                                            //   iconSize: "12px",
                                                            //   mode: "light",
                                                            //   iconWeight: "400",
                                                            //   sx: {
                                                            //     color: "#FFF",
                                                            //     left: "5px",
                                                            //     fontWeight: "400 !important",
                                                            //     fontSize: "12px !important"
                                                            //   }
                                                            // }}
                                                            />
                                                          )}
                                                        <DtvText
                                                        content={channel.name}
                                                          id="channel-name-4"
                                                          sx={{
                                                            ...styles.channelName,
                                                            display: {
                                                              xs: "none",
                                                              sm: "block"
                                                            }
                                                          }}
                                                          variant={
                                                            isChannelLineUpPage ?
                                                              "body2" :
                                                              "smalltextmedium"
                                                          }
                                                        />
                                                        <DtvText
                                                          content={String(
                                                            channel.chnlNum
                                                          )}
                                                          sx={{
                                                            ...styles.channelName,
                                                            ...highLightBgRow,
                                                            display: "block"
                                                          }}
                                                          variant={
                                                            isChannelLineUpPage ?
                                                              "body2" :
                                                              "smalltextmedium"
                                                          }
                                                        />
                                                      </>
                                                    ) : (
                                                      <>
                                                        <CardMedia
                                                          alt={channel.name}
                                                          component="img"
                                                          src={`https://www.directv.com/dtvassets/global/channel-icons/m-n/Channel_Icon_No-Logo.svg`}
                                                          sx={styles.icons}
                                                        />
                                                        <DtvText
                                                        content={channel.name}
                                                          id="channel-name-5"
                                                          variant="smalltextmedium"
                                                          sx={{
                                                            ...styles.channelName,
                                                            display: {
                                                              xs: "none",
                                                              sm: "block"
                                                            }
                                                          }}
                                                        />
                                                        <DtvText
                                                          variant="smalltextmedium"
                                                          content={
                                                            channel.chnlNum
                                                          }
                                                          sx={{
                                                            ...styles.channelName,
                                                            ...highLightBgRow,
                                                            display: "block"
                                                          }}
                                                        />
                                                      </>
                                                    )}
                                                  </DtvBox>
                                                </Tooltip>
                                                {(channel?.category === "LOCAL" || channel?.category === "RSN") && (
                                                  <>
                                                    <DtvButton
                                                      id={`see-details-btn-${channel.chnlNum}-${channelIndex}`}
                                                      label={"See details"}
                                                      variant="uxtext"
                                                      startIcon={
                                                        detailsVisible && selectedChannel === channel.chnlNum ? (
                                                          <DownIcon mode={theme?.grid?.mode} />
                                                        ) : (<UpIcon mode={theme?.grid?.mode} />)
                                                      }
                                                      sx={{
                                                        fontSize: "12px",
                                                        fontWeight: "400",
                                                        textDecoration: "none",
                                                        "& .MuiButton-startIcon":
                                                        {
                                                          margin:
                                                            "0 !important"
                                                        },
                                                        margin: " 0 0 10px 0",
                                                        color: theme && theme?.grid?.textColor ? `${theme?.grid?.textColor} !important` : "#102641"
                                                      }}
                                                      onClick={(e: any) => handleClickChannel(e, channel)}
                                                    />
                                                  </>
                                                )}
                                              </DtvGrid>

                                              {(channel?.category === "LOCAL" || channel?.category === "RSN") ?
                                                props?.packages?.map(
                                                  (packageItem, iconsInd) => {
                                                    const lastColClass =
                                                      iconsInd ===
                                                      props?.packages?.length -
                                                      1 &&
                                                      styles.tableLastCol;
                                                    const tableBodyCatCols1 =
                                                      cmprPkgIndex !== null &&
                                                        cmprPkgIndex + 1 ===
                                                        iconsInd ?
                                                        {
                                                          ...styles.tableColPadNew
                                                        } :
                                                        styles.tableColPad;
                                                    const tableBodyCatCols2 =
                                                      iconsInd === cmprPkgIndex ?
                                                        cmprPkgBorderSelected :
                                                        {};
                                                    const tableBodyCatCols = {
                                                      ...tableBodyCatCols1,
                                                      ...tableBodyCatCols2,
                                                      ...lastColClass,
                                                      ...styles.borderCol,
                                                      ...styles.borderRow
                                                      // ...styles.singlePkgMaxWidth,
                                                    };
                                                    const singlePkg = customPkgIndex != null && props?.packages?.[0]?.name?.toLowerCase();
                                                    const categoryChannelRow = (
                                                      <DtvGrid
                                                        item
                                                        alignItems="center"
                                                        component={TableCell}
                                                        id="tableCell"
                                                        justifyContent="center"
                                                        key={iconsInd}
                                                        style={{
                                                          backgroundColor: customPkgIndex != null ? bgColors[singlePkg] :
                                                            iconsInd ===
                                                            cmprPkgIndex &&
                                                            !(
                                                              highlightRow &&
                                                              channelsQP === ""
                                                            ) &&
                                                            bgColors[
                                                            props?.packages[
                                                              iconsInd
                                                            ].name.toLowerCase()
                                                            ]
                                                        }}
                                                        sx={{
                                                          padding: {
 xs: "10px",
sm: "16px"
},
                                                          ...tableBodyCatCols,
                                                          ...styles.paper,
                                                          ...highLightBgRow,
                                                          // ...styles.tableBodyRowHeight,
                                                          display: "flex",
                                                          flexDirection: "column",
                                                          alignItems: "center",
                                                          justifyContent: "center",
                                                          ...mainColWidthStyle,
                                                          // [breakpoints.up(breakpoints.values.sm)]: {
                                                          //   maxWidth: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                          //   flexBasis: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                          // },
                                                          // [breakpoints.between(breakpoints.values.sm,breakpoints.values.md)]: {
                                                          //   maxWidth: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                          //   flexBasis: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                          // },
                                                          [breakpoints.down(breakpoints.values.sm)]: {
                                                            maxWidth: isChannelLineUpPage && {
                                                              xs: "17% !important"
                                                            }
                                                          },
                                                          [breakpoints.down(breakpoints.values.sm)]: {
                                                            maxWidth: "100%"
                                                          }
                                                        }}
                                                      >
                                                        <Tooltip
                                                          enterTouchDelay={0}
                                                          placement="top"
                                                          PopperProps={{
                                                            sx: {
                                                              marginBottom:
                                                                "-6px !important",
                                                              width:
                                                                "auto !important",
                                                              fontSize:
                                                                "16px !important"
                                                            }
                                                          }}
                                                          title={getTooltipContent(
                                                            `${commonData.channelIncludedText} in`,
                                                            `${props?.packages
                                                              .length === 1 ?
                                                              props
                                                                ?.packages[0]
                                                                ?.name :
                                                              props
                                                                ?.packages[
                                                                iconsInd
                                                              ]?.name
                                                            }`
                                                          )}
                                                        >
                                                          {(channel?.category === "RSN" && !channel?.packageNames?.find(pkgName => pkgName?.toLowerCase().includes(packageItem?.billingCodeLabel?.toLowerCase()))) || (channel?.category === "LOCAL" && packageItem?.id === "ATT_TV_MYSPORTSBETA" && !localMySportsGenreChannels?.includes(channel?.name)) ? null : (
                                                            <DtvBox component="span">
                                                              <CardMedia
                                                                component="img"
                                                                alt={
                                                                  // eslint-disable-next-line no-constant-binary-expression
                                                                  `${commonData.channelIncludedText} in ${props?.packages[iconsInd]?.name}` ||
                                                                  `Included in ${props?.packages[iconsInd]?.name}`
                                                                }
                                                                src={
                                                                  theme && theme?.grid?.mode === "dark" ? "/assets/content/4o3fwhj9oa/svg/included-onDark.svg" : "/assets/content/enyud976rg/svg/included-onLight.svg"
                                                                }
                                                                sx={{
                                                                  ...styles.icons12
                                                                }}
                                                              />
                                                            </DtvBox>
                                                          )}
                                                        </Tooltip>
                                                      </DtvGrid>
                                                    );
                                                    if (customPackage.length === 1) {
                                                      return (
                                                        iconsInd ===
                                                        customPkgIndex &&
                                                        categoryChannelRow
                                                      );
                                                    }
                                                      return categoryChannelRow;
                                                  }
                                                )                                                :
                                                props?.packages?.map?.(
                                                  (packageItem, pkgIndex) => {
                                                    // WAT-11878: read the indicator from the pkg's OWN language slot so
                                                    // the cmprPkg-injected column (e.g. Óptimo Más, categoryId=SPANISH)
                                                    // doesn't inherit the current view's package's check-marks.
                                                    const packageIndicator = getPkgIndicator(channel, packageItem, !!props?.edspFlow, pkgCtgySelected_upc);
                                                    const iconsInd = pkgIndex;
                                                    const lastColClass =
                                                      iconsInd ===
                                                      props?.packages?.length -
                                                      1 &&
                                                      styles.tableLastCol;
                                                    const tableBodyCatCols1 =
                                                      cmprPkgIndex !== null &&
                                                        cmprPkgIndex + 1 ===
                                                        iconsInd ?
                                                        {
                                                          ...styles.tableColPadNew
                                                        } :
                                                        styles.tableColPad;
                                                    const tableBodyCatCols2 =
                                                      iconsInd === cmprPkgIndex ?
                                                        cmprPkgBorderSelected :
                                                        {};
                                                    const tableBodyCatCols = {
                                                      ...tableBodyCatCols1,
                                                      ...tableBodyCatCols2,
                                                      ...lastColClass,
                                                      ...styles.borderCol,
                                                      ...styles.borderRow
                                                      // ...styles.singlePkgMaxWidth,
                                                    };
                                                    const packageId =
                                                      props?.packages?.length ===
                                                        1 ?
                                                        props?.packages[0]?.id?.toUpperCase() :
                                                        props?.packages[
                                                          iconsInd
                                                        ]?.id?.toUpperCase();
                                                    let billingCodeLabel = "";
                                                    if (!isChannelLineUpPage) {
                                                      if (packageId?.includes("ATT_TV_")) {
                                                        // eslint-disable-next-line prefer-destructuring
                                                        billingCodeLabel = packageId.split("ATT_TV_")[1];
                                                      } else if (packageId?.includes("DTV_")) {
                                                        // eslint-disable-next-line prefer-destructuring
                                                        billingCodeLabel = packageId.split("DTV_")[1];
                                                      }
                                                    } else {
                                                      if (packageId?.includes("ATT_TV_")) {
                                                        // eslint-disable-next-line prefer-destructuring
                                                        billingCodeLabel = packageId.split("ATT_TV_")[1];
                                                      }
                                                    }
                                                    const highlightPkg =
                                                      (channel?.includedChnlPackages?.includes(`ATT_TV_${billingCodeLabel}`) &&
                                                        !channel?.includedChnlPackages?.includes(`DTV_${billingCodeLabel}`)) ||
                                                      (!channel?.includedChnlPackages?.includes(`ATT_TV_${billingCodeLabel}`) &&
                                                        channel?.includedChnlPackages?.includes(`DTV_${billingCodeLabel}`));
                                                    const labelText = !channel?.includedChnlPackages?.includes(`ATT_TV_${billingCodeLabel}`) &&
                                                      channel?.includedChnlPackages?.includes(`DTV_${billingCodeLabel}`) ?
                                                      "Satellite Only" :
                                                      "Internet Only";
                                                    let showPkg = true;
                                                    if (channelsQP !== "") {
                                                      if (!isChannelLineUpPage) {
                                                        if (
                                                          (channelsQP === "internet" &&
                                                            !channel?.includedChnlPackages?.includes(`ATT_TV_${billingCodeLabel}`)) ||
                                                          (channelsQP === "satellite" &&
                                                            !channel?.includedChnlPackages?.includes(`DTV_${billingCodeLabel}`))
                                                        ) {
                                                          showPkg = false;
                                                        }
                                                      } else {
                                                        if (
                                                          channelsQP === "internet" && !channel?.includedChnlPackages?.includes(`ATT_TV_${billingCodeLabel}`)
                                                        ) {
                                                          showPkg = false;
                                                        }
                                                      }
                                                    }
                                                    const _highlightLabel =
                                                      !highlightRow &&
                                                      highlightPkg &&
                                                      channelsQP !==
                                                      labelText.toLowerCase();
                                                    const singlePkg = customPkgIndex != null && props?.packages?.[0]?.name?.toLowerCase();
                                                    const categoryChannelRow = (
                                                      <DtvGrid
                                                        item
                                                        alignItems="center"
                                                        component={TableCell}
                                                        id="tableCell"
                                                        justifyContent="center"
                                                        key={iconsInd}
                                                        style={{
                                                          backgroundColor: customPkgIndex != null ? bgColors[singlePkg] :
                                                            iconsInd === cmprPkgIndex &&
                                                            !(highlightRow && channelsQP === "") &&
                                                            bgColors[props?.packages[iconsInd]?.name?.toLowerCase()]
                                                        }}
                                                        sx={{
                                                          padding: {
 xs: "10px",
sm: "16px"
},
                                                          ...tableBodyCatCols,
                                                          ...styles.paper,
                                                          ...highLightBgRow,
                                                          // ...styles.tableBodyRowHeight,
                                                          display: "flex",
                                                          flexDirection: "column",
                                                          alignItems: "center",
                                                          justifyContent: "center",
                                                          ...mainColWidthStyle,
                                                          // [breakpoints.up(breakpoints.values.sm)]: {
                                                          //   maxWidth: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                          //   flexBasis: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                          // },
                                                          // [breakpoints.between(breakpoints.values.sm,breakpoints.values.md)]: {
                                                          //   maxWidth: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                          //   flexBasis: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                          // },
                                                          [breakpoints.down(breakpoints.values.sm)]: {
                                                            maxWidth: "100%"
                                                          }
                                                        }}
                                                      >
                                                        {packageIndicator === PACKAGE_INDICATORS.IN && showPkg && (
                                                          <Tooltip
                                                            enterTouchDelay={0}
                                                            placement="top"
                                                            PopperProps={{
                                                              sx: {
                                                                marginBottom: "-6px !important",
                                                                width: "auto !important",
                                                                fontSize: "16px !important"
                                                              }
                                                            }}
                                                            title={getTooltipContent(
                                                              `${commonData.channelIncludedText} in`,
                                                              `${props?.packages.length === 1 ?
                                                                props?.packages[0]?.name :
                                                                props?.packages[iconsInd]?.name
                                                              }`
                                                            )}
                                                          >
                                                            <DtvBox component="span">
                                                              <CardMedia
                                                                component="img"
                                                                src={theme && theme?.grid?.mode === "dark" ? "/assets/content/4o3fwhj9oa/svg/included-onDark.svg" : "/assets/content/enyud976rg/svg/included-onLight.svg"}
                                                                sx={{ ...styles.icons12 }}
                                                                alt={
                                                                  // eslint-disable-next-line no-constant-binary-expression
                                                                  `${commonData.channelIncludedText} in ${props?.packages[iconsInd]?.name}` ||
                                                                  `Included in ${props?.packages[iconsInd]?.name}`
                                                                }
                                                              />
                                                            </DtvBox>
                                                          </Tooltip>
                                                        )}
                                                      </DtvGrid>
                                                    );
                                                    if (customPackage.length === 1) {
                                                      return (
                                                        iconsInd ===
                                                        customPkgIndex &&
                                                        categoryChannelRow
                                                      );
                                                    }
                                                      return categoryChannelRow;
                                                  }
                                                )}
                                            </DtvGrid>
                                            {(channel?.category === "LOCAL" ||
                                              channel?.category === "RSN") &&
                                              detailsVisible &&
                                              selectedChannel ===
                                              channel.chnlNum && (
                                                <DtvGrid
                                                  container
                                                  component={TableRow}
                                                  id={`row-${channel.chnlNum}-${channelIndex}-see-detials-section`}
                                                  key={`see-detail-${channelIndex}`}
                                                  sx={{ width: "100%" }}
                                                >
                                                  <TableCell
                                                    colSpan={5}
                                                    sx={{
                                                      border: "none !important",
                                                      borderBottom:
                                                        "1px solid #102641 !important",
                                                      padding: "0px !important",
                                                      width: "100%"
                                                    }}
                                                  >
                                                    {
                                                      <DtvBox
                                                        sx={{
                                                          // position: 'absolute',

                                                          // left: "0px",
                                                          // bottom : "0",
                                                          background: "white"

                                                          // width : `${tableRef?.current?.offsetWidth}px` || 0
                                                        }}
                                                      >
                                                        {channel?.category ===
                                                          "LOCAL" ? (
                                                          <DtvText
                                                            content={
                                                              "Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them."
                                                            }
                                                            sx={{
                                                              padding:
                                                                "4px 0 4px 0",
                                                              fontFamily:
                                                                "roboto-regular",
                                                              fontSize:
                                                                "12px !important",
                                                              paddingLeft:
                                                                // eslint-disable-next-line no-constant-binary-expression
                                                                `${document.getElementById(`see-details-btn-${channel.chnlNum}-${channelIndex}`)
                                                                  ?.getBoundingClientRect?.()?.x
                                                                }px` || "20px"
                                                            }}
                                                          />
                                                        ) : (
                                                          <>
                                                            {channel?.mlb || channel?.nba || channel?.nhl || channel?.mls || channel?.wnba ? (
                                                              <DtvBox
                                                                sx={{
                                                                  ...styles.teamsContainer,
                                                                  paddingLeft:
                                                                    // eslint-disable-next-line no-constant-binary-expression
                                                                    `${document.getElementById(`see-details-btn-${channel.chnlNum}-${channelIndex}`)
                                                                      ?.getBoundingClientRect?.()?.x
                                                                    }px` ||
                                                                    "20px"
                                                                }}
                                                              >
                                                                <DtvText
                                                                  content="Teams:"
                                                                  variant={"body3medium"}
                                                                ></DtvText>
                                                                <DtvBox
                                                                  component="span"
                                                                  sx={{
                                                                    color: "white",
                                                                    // margin: "0 0 0 16px",
                                                                    ...styles.teamsContainer,
                                                                    paddingLeft:
                                                                      // eslint-disable-next-line no-constant-binary-expression
                                                                      `${document
                                                                        .getElementById(
                                                                          `see-details-btn-${channel.chnlNum}-${channelIndex}`
                                                                        )
                                                                        ?.getBoundingClientRect?.()
                                                                        ?.x
                                                                      }px` ||
                                                                      "20px"
                                                                    }}
                                                                >
                                                                  <DtvText
                                                                    content={getRsnTeams(channel)}
                                                                    id="mlb-team"
                                                                    variant="body3"
                                                                  />
                                                                </DtvBox>
                                                              </DtvBox>
                                                            ) : null}
                                                            <DtvText
                                                              content={`<sup>${rsnChnlsObj?.[channel?.callSign]?.rsntier === 1 ? "" : rsnChnlsObj?.[channel?.callSign]?.rsntier}</sup> ${rsnTierLegalTextObj?.[0]?.[props?.channelsQP?.toLowerCase() === "satellite" ? "satellite" : "stream"]?.[`tier${rsnChnlsObj?.[channel?.callSign]?.rsntier}`]}`}
                                                              sx={{
                                                                padding:
                                                                  "10px 0 10px 0",
                                                                fontFamily:
                                                                  "roboto-regular",
                                                                fontSize:
                                                                  "12px !important",
                                                                paddingLeft:
                                                                  // eslint-disable-next-line no-constant-binary-expression
                                                                  `${document
                                                                    .getElementById(
                                                                      `see-details-btn-${channel.chnlNum}-${channelIndex}`
                                                                    )
                                                                    ?.getBoundingClientRect?.()
                                                                    ?.x
                                                                  }px` ||
                                                                  "20px"
                                                              }}
                                                            />
                                                          </>
                                                        )}
                                                      </DtvBox>
                                                    }
                                                  </TableCell>
                                                </DtvGrid>
                                              )}
                                          </>
                                        );

                                        //   WAT - 4258
                                          // if(!msbNationalRollOut){
                                           props?.packages?.forEach(
                                            (packageItem, _iconsInd) => {
                                              if((channel?.category === "RSN" && props?.packages.length === 1 && ["ATT_TV_MYSPORTSBETA"]?.includes(packageItem?.id)) || (channel?.category === "LOCAL" &&  props?.packages.length === 1 && packageItem?.id === "ATT_TV_MYSPORTSBETA" && !localMySportsGenreChannels?.includes(channel?.name))){
                                                showChannel = false;
                                              }
                                            }
)
                                          // }
                                      //   WAT - 4258

                                        if (showChannel) {
                                          if (channelIndex <= 15) {
                                            return (
                                              <>{accordionChannelRowElement}</>
                                            );
                                          }
                                            return (
                                              <LazyLoad
                                                height={"60px"}
                                                key={channelIndex}
                                                offset={500}
                                                once={true}
                                              >
                                                {accordionChannelRowElement}
                                              </LazyLoad>
                                            );
                                        }
                                        return null;
                                      }
                                    )}

                                  {isCommonCategory && addOnChnls.map((channel, channelIndex) => {
                                    const firstRow =
                                      channelIndex === 0 ?
                                        styles.tablefirstRowCat :
                                        styles.tablerowContainer;
                                    // /
                                    // eslint-disable-next-line camelcase
                                    const channelsQP = channelsQP_lwc || "";
                                    const highlightRow =
                                      (channel?.isSatelliteAddonPromo &&
                                        !channel?.isStreamAddonPromo) ||
                                      (!channel?.isSatelliteAddonPromo &&
                                        channel?.isStreamAddonPromo);
                                    const highLightBgRow =
                                      !isChannelLineUpPage &&
                                        highlightRow &&
                                        channelsQP === "" ?
                                        styles.highlightRowBgColor :
                                        {};
                                    let showChannel = true;
                                    if (channelsQP !== "") {
                                      // NOTE: Considering the any channel which is addon or promo for any package will also be addon or promo for Entertainment
                                      const entertainmentPkg = props?.packages?.find(p => DEFAULT_ADDON_PROMO_PACKAGES.includes(p.id?.toUpperCase()));
                                      const packageId = entertainmentPkg ? entertainmentPkg?.id?.toUpperCase() : props?.packages[0]?.id?.toUpperCase();

                                      let billingCodeLabel = "";
                                      if (!isChannelLineUpPage) {
                                        if (packageId?.includes("ATT_TV_")) {
                                          // eslint-disable-next-line prefer-destructuring
                                          billingCodeLabel =
                                            packageId.split("ATT_TV_")[1];
                                        } else if (
                                          packageId?.includes("DTV_")
                                        ) {
                                          // eslint-disable-next-line prefer-destructuring
                                          billingCodeLabel =
                                            packageId.split("DTV_")[1];
                                        }
                                        if (
                                          (channelsQP === "internet" &&
                                            !channel?.addonPromoChnlPackages?.includes(
                                              `ATT_TV_${billingCodeLabel}`
                                            )) ||
                                          (channelsQP === "satellite" &&
                                            !channel?.addonPromoChnlPackages?.includes(
                                              `DTV_${billingCodeLabel}`
                                            ))
                                        ) {
                                          showChannel = false;
                                        }
                                      } else {
                                        if (packageId?.includes("ATT_TV_")) {
                                          // eslint-disable-next-line prefer-destructuring
                                          billingCodeLabel =
                                            packageId.split("ATT_TV_")[1];
                                        }
                                        if (
                                          channelsQP === "internet" &&
                                          !channel?.addonPromoChnlPackages?.includes(
                                            `ATT_TV_${billingCodeLabel}`
                                          )
                                        ) {
                                          showChannel = false;
                                        }
                                      }
                                    }
                                    const accordionChannelRowElement = (
                                      <DtvGrid
                                        container
                                        component={TableRow}
                                        id="nestedTableRow"
                                        key={channelIndex}
                                        sx={{
                                          width: "100%",

                                          backgroundColor: isChannelLineUpPage ?
                                            "#21528C" :
                                            theme && theme?.grid?.background ? theme?.grid?.background : colorPalette.neutral_white.main,
                                          ...styles.tableBorder,
                                          firstRow,
                                          ...styles.marginRow,
                                          ...styles.accordionRow
                                        }}
                                      >
                                        {!isChannelLineUpPage &&
                                          highlightRow &&
                                          channelsQP === "" && (
                                            <DtvBox
                                              component={TableCell}
                                              id="tableCell"
                                              sx={{
                                                padding: "0px !important",
                                                width: "100%",
                                                backgroundColor: theme && theme?.grid?.highLightRowBackground ? theme?.grid?.highLightRowBackground : '#D1D1D1',
                                                color: theme && theme?.grid?.highLightRowTextColor ? `${theme?.grid?.highLightRowTextColor} !important` : "#000000",
                                                borderBottom: `${isChannelLineUpPage ?
                                                    "1" :
                                                    "2"
                                                  }px solid ${isChannelLineUpPage ?
                                                    "#D1D1D1" :
                                                    theme && theme?.grid?.border ? theme?.grid?.border : "#999999"
                                                  }`,
                                                position: "relative",
                                                textAlign: "center",
                                                height: {
                                                  xs: "18px",
                                                  sm: "22px"
                                                },
                                                lineHeight: {
                                                  xs: "1.3em",
                                                  sm: "1.7em"
                                                },
                                                top:
                                                  channelIndex === 0 && "8px",
                                                [breakpoints.between(
                                                  breakpoints.values
                                                    .xs,
                                                  breakpoints.values
                                                    .xl
                                                )]: {
                                                  fontSize: "14px !important"
                                                },
                                                "@media (min-width: 1700px)": {
                                                  height: "32px"
                                                }
                                              }}
                                            >
                                              {`Exclusively on ${channel?.isSatelliteAddonPromo ?
                                                  "Satellite" :
                                                  "Internet"
                                                }`}
                                            </DtvBox>
                                          )}
                                        <DtvGrid
                                          item
                                          alignItems="center"
                                          component={TableCell}
                                          id="tableCell"
                                          justifyContent="center"
                                          key={`logo${channelIndex}`}
                                          sx={{
                                            ...styles.tableColPad,
                                            ...styles.borderRow,
                                            ...styles.paper,
                                            ...highLightBgRow,
                                            // ...styles.tableBodyRowHeight,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            ...channelColWidthStyle,
                                            [breakpoints.down(breakpoints.values.sm)]: {
                                              minWidth: isChannelLineUpPage && {
                                                xs: "32% !important"
                                              }
                                            }
                                          }}
                                        >
                                          <Tooltip
                                            enterTouchDelay={0}
                                            placement="top"
                                            PopperProps={{
                                              sx: {
                                                marginBottom:
                                                  "-25px !important",
                                                width: "auto !important",
                                                fontSize: "16px !important"
                                              }
                                            }}
                                            title={getTooltipContent(
                                              channel.name
                                            )}
                                          >
                                            <DtvBox component="span">
                                              {channel.icon ? (
                                                <>
                                                  <ChannelLogoChip
                                                    alt={channel.name}
                                                    component="span"
                                                    display="inline-flex"
                                                    imgSx={styles.icons}
                                                    margin="6px 0"
                                                    size="64px"
                                                    isOvLogo={channel?.isOvLogo}
                                                    src={channel.icon}
                                                    verticalAlign="middle"
                                                  />
                                                  <DtvText
                                                  content={channel.name}
                                                    id="channel-name-6"
                                                    variant="smalltextmedium"
                                                    sx={{
                                                      ...styles.channelName,
                                                      display: "block"
                                                    }}
                                                  />
                                                  <DtvText
                                                    variant="smalltextmedium"
                                                    content={String(
                                                      channel.chnlNum
                                                    )}
                                                    sx={{
                                                      ...styles.channelName,
                                                      ...highLightBgRow,
                                                      display: "block"
                                                    }}
                                                  />
                                                </>
                                              ) : (
                                                <>
                                                  <CardMedia
                                                    alt={channel.name}
                                                    component="img"
                                                    src={`https://www.directv.com/dtvassets/global/channel-icons/m-n/Channel_Icon_No-Logo.svg`}
                                                    sx={styles.icons}
                                                  />
                                                  <DtvText
                                                  content={channel.name}
                                                    id="channel-name-6"
                                                    variant="smalltextmedium"
                                                    sx={{
                                                      ...styles.channelName,
                                                      display: "block"
                                                    }}
                                                  />
                                                  <DtvText
                                                    variant="smalltextmedium"
                                                    content={String(
                                                      channel.chnlNum
                                                    )}
                                                    sx={{
                                                      ...styles.channelName,
                                                      ...highLightBgRow,
                                                      display: "block"
                                                    }}
                                                  />
                                                </>
                                              )}
                                            </DtvBox>
                                          </Tooltip>
                                        </DtvGrid>
                                        {props?.packages?.map?.(
                                          (packageItem, pkgIndex) => {
                                            // WAT-11878: add-on row — read from `addOnPkgIndicators` AND from the
                                            // pkg's own language slot so the cmprPkg-injected column doesn't
                                            // silently render as not-included on the Included Subscriptions block.
                                            const packageIndicator = getPkgIndicator(channel, packageItem, !!props?.edspFlow, pkgCtgySelected_upc, "addon");
                                            const iconsInd = pkgIndex;
                                            const lastColClass =
                                              iconsInd ===
                                              props?.packages?.length -
                                              1 && styles.tableLastCol;
                                            const tableBodyCatCols1 =
                                              cmprPkgIndex !== null &&
                                                cmprPkgIndex + 1 === iconsInd ?
                                                { ...styles.tableColPadNew } :
                                                styles.tableColPad;
                                            const tableBodyCatCols2 =
                                              iconsInd === cmprPkgIndex ?
                                                cmprPkgBorderSelected :
                                                {};
                                            const tableBodyCatCols = {
                                              ...tableBodyCatCols1,
                                              ...tableBodyCatCols2,
                                              ...lastColClass,
                                              ...styles.borderCol,
                                              ...styles.borderRow
                                              // ...styles.singlePkgMaxWidth,
                                            };
                                            const packageId =
                                              props?.packages?.length === 1 ?
                                                props?.packages[0]?.id?.toUpperCase() :
                                                props?.packages[
                                                  iconsInd
                                                ]?.id?.toUpperCase();
                                            let billingCodeLabel = "";
                                            if (!isChannelLineUpPage) {
                                              if (
                                                packageId?.includes("ATT_TV_")
                                              ) {
                                                // eslint-disable-next-line prefer-destructuring
                                                billingCodeLabel =
                                                  packageId.split(
                                                    "ATT_TV_"
                                                  )[1];
                                              } else if (
                                                packageId?.includes("DTV_")
                                              ) {
                                                // eslint-disable-next-line prefer-destructuring
                                                billingCodeLabel =
                                                  packageId.split("DTV_")[1];
                                              }
                                            } else {
                                              if (
                                                packageId?.includes("ATT_TV_")
                                              ) {
                                                // eslint-disable-next-line prefer-destructuring
                                                billingCodeLabel =
                                                  packageId.split(
                                                    "ATT_TV_"
                                                  )[1];
                                              }
                                            }
                                            const highlightPkg =
                                              (channel?.addonPromoChnlPackages?.includes(
                                                `ATT_TV_${billingCodeLabel}`
                                              ) &&
                                                !channel?.addonPromoChnlPackages?.includes(
                                                  `DTV_${billingCodeLabel}`
                                                )) ||
                                              (!channel?.addonPromoChnlPackages?.includes(
                                                `ATT_TV_${billingCodeLabel}`
                                              ) &&
                                                channel?.addonPromoChnlPackages?.includes(
                                                  `DTV_${billingCodeLabel}`
                                                ));
                                            const labelText =
                                              !channel?.addonPromoChnlPackages?.includes(
                                                `ATT_TV_${billingCodeLabel}`
                                              ) &&
                                                channel?.addonPromoChnlPackages?.includes(
                                                  `DTV_${billingCodeLabel}`
                                                ) ?
                                                "Satellite Only" :
                                                "Internet Only";
                                            const highlightLabel =
                                              !highlightRow &&
                                              highlightPkg &&
                                              channelsQP !==
                                              labelText.toLowerCase();
                                            const singlePkg = customPkgIndex != null && props?.packages?.[0]?.name?.toLowerCase();
                                            const categoryChannelRow = (
                                              <DtvGrid
                                                item
                                                alignItems="center"
                                                component={TableCell}
                                                id="tableCell"
                                                justifyContent="center"
                                                key={iconsInd}
                                                style={{
                                                  backgroundColor: customPkgIndex != null ?
                                                    bgColors[singlePkg] :
                                                    iconsInd === cmprPkgIndex &&
                                                    !(highlightRow && channelsQP === "") &&
                                                    bgColors[props?.packages[iconsInd].name.toLowerCase()]
                                                }}
                                                sx={{
                                                  ...tableBodyCatCols,
                                                  ...styles.paper,
                                                  ...highLightBgRow,
                                                  // ...styles.tableBodyRowHeight,
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  justifyContent: "center",
                                                  alignItems: "center",
                                                  // [breakpoints.up(breakpoints.values.sm)]: {
                                                  //   maxWidth: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                  //   flexBasis: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                  // },
                                                  // [breakpoints.between(breakpoints.values.sm,breakpoints.values.md)]: {
                                                  //   maxWidth: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                  //   flexBasis: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                  // },
                                                  ...mainColWidthStyle,
                                                  [breakpoints.down(breakpoints.values.sm)]: {
                                                    maxWidth: isChannelLineUpPage && {
                                                      xs: "17% !important"
                                                    }
                                                  },
                                                  [breakpoints.down(breakpoints.values.sm)]: {
                                                    maxWidth: "100%"
                                                  }
                                                }}
                                              >
                                                {packageIndicator ===
                                                  PACKAGE_INDICATORS.IN && (
                                                    <Tooltip
                                                      enterTouchDelay={0}
                                                      placement="top"
                                                      PopperProps={{
                                                        sx: {
                                                          marginBottom:
                                                            "-6px !important",
                                                          width:
                                                            "auto !important",
                                                          fontSize:
                                                            "16px !important"
                                                        }
                                                      }}
                                                      title={getTooltipContent(
                                                        `${commonData.channelIncludedText} in`,
                                                        `${props?.packages[iconsInd]?.name}`
                                                      )}
                                                    >
                                                      <DtvBox
                                                        sx={{
                                                          display:
                                                            highlightLabel &&
                                                            !channelsQP &&
                                                            "inline-block",
                                                          paddingTop:
                                                            highlightLabel &&
                                                            !channelsQP &&
                                                            "22px"
                                                        }}
                                                      >
                                                        <CardMedia
                                                          component="img"
                                                          alt={
                                                            // eslint-disable-next-line no-constant-binary-expression
                                                            `${commonData.channelIncludedText} in ${props?.packages[iconsInd]?.name}` ||
                                                            `Included in ${props?.packages[iconsInd]?.name}`
                                                          }
                                                          src={
                                                            theme && theme?.grid?.mode === "dark" ? "/assets/content/4o3fwhj9oa/svg/included-onDark.svg" : "/assets/content/enyud976rg/svg/included-onLight.svg"
                                                          }
                                                          sx={{
                                                            ...styles.icons12
                                                          }}
                                                        />
                                                      </DtvBox>
                                                    </Tooltip>
                                                  )}
                                                {packageIndicator ===
                                                  PACKAGE_INDICATORS.ADDON && (
                                                    <Tooltip
                                                      enterTouchDelay={0}
                                                      placement="top"
                                                      PopperProps={{
                                                        sx: {
                                                          marginBottom:
                                                            "-6px !important",
                                                          width:
                                                            "auto !important",
                                                          fontSize:
                                                            "16px !important"
                                                        }
                                                      }}
                                                      title={getTooltipContent(
                                                        `${commonData.channelAddOntext} in`,
                                                        `${props?.packages[iconsInd]?.name}`
                                                      )}
                                                    >
                                                      <DtvBox
                                                        sx={{
                                                          display:
                                                            highlightLabel &&
                                                            !channelsQP &&
                                                            "inline-block",
                                                          paddingTop:
                                                            highlightLabel &&
                                                            !channelsQP &&
                                                            "22px"
                                                        }}
                                                      >
                                                        <CardMedia
                                                          component="img"
                                                          alt={
                                                            // eslint-disable-next-line no-constant-binary-expression
                                                            `${commonData.channelAddOntext} in ${props?.packages[iconsInd]?.name}` ||
                                                            `Premium channel in ${props?.packages[iconsInd]?.name}`
                                                          }
                                                          src={
                                                            theme && theme?.grid?.mode === "dark" ? "/assets/content/dvwlcmuqjk/svg/addon-onDark.svg" : "/assets/content/pr32m1cdgf/svg/addon-onLight.svg"
                                                          }
                                                          sx={{
                                                            ...styles.icons12
                                                          }}
                                                        />

                                                      </DtvBox>
                                                    </Tooltip>
                                                  )}
                                                {packageIndicator ===
                                                  PACKAGE_INDICATORS.PROMO && (
                                                    <Tooltip
                                                      enterTouchDelay={0}
                                                      placement="top"
                                                      PopperProps={{
                                                        sx: {
                                                          marginBottom:
                                                            "-6px !important",
                                                          width:
                                                            "auto !important",
                                                          fontSize:
                                                            "16px !important"
                                                        }
                                                      }}
                                                      title={getTooltipContent(
                                                        `${commonData.channelPromoText} in`,
                                                        `${props?.packages[iconsInd]?.name}`
                                                      )}
                                                    >
                                                      <DtvBox
                                                        sx={{
                                                          display:
                                                            highlightLabel &&
                                                            !channelsQP &&
                                                            "inline-block",
                                                          paddingTop:
                                                            highlightLabel &&
                                                            !channelsQP &&
                                                            "22px"
                                                        }}
                                                      >
                                                        <CardMedia
                                                          component="img"
                                                          alt={
                                                            // eslint-disable-next-line no-constant-binary-expression
                                                            `${commonData.channelPromoText} in ${props?.packages[iconsInd]?.name}` ||
                                                            `Promo available in ${props?.packages[iconsInd]?.name}`
                                                          }
                                                          src={
                                                            theme && theme?.grid?.mode === "dark" ? "/assets/content/hni5xiiqjh/svg/promo-onDark.svg" : "/assets/content/joqxso5l7u/svg/promo-onLight.svg"
                                                          }
                                                          sx={{
                                                            ...styles.icons12
                                                          }}
                                                        />
                                                      </DtvBox>
                                                    </Tooltip>
                                                  )}
                                              </DtvGrid>
                                            );
                                            if (customPackage.length === 1) {
                                              return (
                                                iconsInd ===
                                                customPkgIndex &&
                                                categoryChannelRow
                                              );
                                            }
                                              return categoryChannelRow;
                                          }
                                        )}
                                      </DtvGrid>
                                    );
                                    if (showChannel) {
                                      if (channelIndex <= 15) {
                                        return (
                                          <>{accordionChannelRowElement}</>
                                        );
                                      }
                                        return (
                                          <LazyLoad
                                            height={"60px"}
                                            key={channelIndex}
                                            offset={500}
                                            once={true}
                                          >
                                            {accordionChannelRowElement}
                                          </LazyLoad>
                                        );
                                    }
                                    return null;
                                  })}
                                </>)}
                                </TableBody>
                              </Table>
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      </TableCell>
                    </TableRow>
                  )
                );
              }
              return null;
            })}
          {props?.viewMode === CHANNEL_LINEUP_VIEW.CATEGORY && !isSearchActive &&
            props?.channels &&
            updatedChnlCategories &&
            updatedChnlCategories.map((category, index) => {
              if (category.key === "SPORTS") {
                category.value = "National sports";
              }
              const isCommonCategory = category.key === "ADD_ON_CHANNELS";
              // eslint-disable-next-line camelcase
              const nct_uniq = uniqId();
              // Computed once here — reused for both the accordion height fix
              // (below) and the subheading render gate, instead of re-deriving
              // category.key === "MYFREEDTV_CHANNELS" && isSatelliteQP at each site.
              const showMyFreeSubheading = category.key === "MYFREEDTV_CHANNELS" && isSatelliteQP && Boolean(commonData?.myFreeDtvSubheading);
              // Multi-membership (mirrors /channel-lineup): a channel renders under a
              // CONTENT accordion when the server-built membership map lists it there, OR
              // (LOCAL/RSN/ADD_ON/MyFree/INCLUDED + flag-OFF) by scalar category equality.
              // Build the membership ids into a Set once per category so the filter does
              // O(1) lookups instead of a linear .includes() scan per channel.
              const categoryMemberSet = channelCategoryMembership?.[category.key]
                ? new Set(channelCategoryMembership[category.key])
                : undefined;
              let categoryChannels = channels.filter(
                chn =>
                  (categoryMemberSet?.has(chn.id) ||
                    chn.category === category.key) &&
                  (chn?.isMyFreeDTV === undefined || chn?.isMyFreeDTV === false)
              );
              if (category.key === "MYFREEDTV_CHANNELS") {
                categoryChannels = channels.filter(
                  chn => chn?.isMyFreeDTV === true
                );
              }
              if (isCommonCategory || categoryChannels.length > 0 || category.key === "INCLUDED_SUBSCRIPTIONS") {
                if (!isCommonCategory) {
                  const addOnChnls = categoryChannels.filter(channel => {
                    const pkgIndicators =
                      (channel &&
                        channel.packageIndicators &&
                        channel.packageIndicators[
                        `${props?.edspFlow ? "edsp" : "2yr"}`
                        // eslint-disable-next-line camelcase
                        ][pkgCtgySelected_upc]) ||
                      [];
                    const addOnPkgIndicators =
                      (channel &&
                        channel.addOnPkgIndicators &&
                        channel.addOnPkgIndicators[
                        `${props?.edspFlow ? "edsp" : "2yr"}`
                        // eslint-disable-next-line camelcase
                        ][pkgCtgySelected_upc]) ||
                      [];
                    const isAddOn =
                      addOnPkgIndicators.filter(x => x !== null).length > 0;
                    return (
                      isAddOn ||
                      pkgIndicators.includes(PACKAGE_INDICATORS.ADDON) ||
                      pkgIndicators.includes(PACKAGE_INDICATORS.PROMO)
                    );
                  });
                  commonCategory[category.key] = addOnChnls;
                  commonCategoryChannelsArray = [
                    ...commonCategoryChannelsArray,
                    ...addOnChnls
                  ];
                  if (!displayCommonCategory) {
                    displayCommonCategory =
                      addOnChnls.length > 0;
                  }
                }
                if (customPkgIndex !== null) {
                  categoryChannels = categoryChannels.filter(channel => {
                    const isIncludedInAnyPkg =
                      (props?.packages?.some(
                        // WAT-11878: read the pkg's inclusion from its OWN language slot
                        // (see getPkgIndicator helper) so a cmprPkg-injected column
                        // doesn't force channels to appear based on the current view's
                        // package check-marks.
                        pkg => getPkgIndicator(channel, pkg, !!props?.edspFlow, pkgCtgySelected_upc) === PACKAGE_INDICATORS.IN
                      ) ?? false);
                    return (
                      isIncludedInAnyPkg ||
                      category.key === "LOCAL" ||
                      category.key === "RSN"
                    );
                  });
                } else {
                  categoryChannels = categoryChannels.filter(channel => {
                    const isIncludedInAnyPkg =
                      (props?.packages?.some(
                        // WAT-11878: read the pkg's inclusion from its OWN language slot
                        // (see getPkgIndicator helper) so a cmprPkg-injected column
                        // doesn't force channels to appear based on the current view's
                        // package check-marks.
                        pkg => getPkgIndicator(channel, pkg, !!props?.edspFlow, pkgCtgySelected_upc) === PACKAGE_INDICATORS.IN
                      ) ?? false);
                    return (
                      isIncludedInAnyPkg ||
                      category.key === "LOCAL" ||
                      category.key === "RSN"
                    );
                  });
                }
                let showAccordion = true;
                if (!isCommonCategory) {
                  const notCommonCategoryChnlsCount = categoryChannels
                    .map(channel => {
                      if (
                        channel?.category === "LOCAL" ||
                        channel?.category === "RSN"
                      ) {
 return channel;
}
                      // eslint-disable-next-line camelcase
                      const channelsQP = channelsQP_lwc || "";
                      let showChannel = true;
                      if (channelsQP !== "") {
                        showChannel = false;
                        const _packageIndicators =
                          (channel &&
                            channel.packageIndicators &&
                            channel.packageIndicators[
                            `${edspFlow ? "edsp" : "2yr"}`
                            // eslint-disable-next-line camelcase
                            ][pkgCtgySelected_upc]) ||
                          [];
                        props?.packages?.forEach(
                          (packageItem, pkgIndex) => {
                            const iconsInd = pkgIndex;
                            // const packageId = props?.packages[0]?.id?.toUpperCase();
                            const packageId =
                              props?.packages?.length === 1 ?
                                props?.packages[0]?.id?.toUpperCase() :
                                props?.packages[iconsInd]?.id?.toUpperCase();
                            // const packageId = props?.packages[0]?.id?.toUpperCase();
                            let billingCodeLabel = "";
                            if (packageId?.includes("ATT_TV_")) {
                              // eslint-disable-next-line prefer-destructuring
                              billingCodeLabel = packageId.split("ATT_TV_")[1];
                            } else if (packageId?.includes("DTV_")) {
                              // eslint-disable-next-line prefer-destructuring
                              billingCodeLabel = packageId.split("DTV_")[1];
                            }
                            if (
                              (channelsQP === "internet" &&
                                channel?.includedChnlPackages?.includes(
                                  `ATT_TV_${billingCodeLabel}`
                                )) ||
                              (channelsQP === "satellite" &&
                                channel?.includedChnlPackages?.includes(
                                  `DTV_${billingCodeLabel}`
                                ))
                            ) {
                              showChannel = true;
                            }
                            if (channel?.category === "RSN" && !channel?.packageNames?.find(pkgName => pkgName?.toLowerCase().includes(packageItem?.billingCodeLabel?.toLowerCase()))) {
                              // Removing for Entertainment pkg. TO-DO: check and read from packageNames returned by API
                              showChannel = false;
                            }
                          }
);
                      }
                      return showChannel ? channel : false;
                    })
                    .filter(x => x);
                  showAccordion = notCommonCategoryChnlsCount?.length > 0;
                }

                if (isCommonCategory) {
                  const commonCategoryChnlsCount = commonCategoryChannelsArray
                    .map(channel => {
                      // eslint-disable-next-line camelcase
                      const channelsQP = channelsQP_lwc || "";
                      let showChannel = true;
                      if (channelsQP !== "") {
                        // NOTE: Considering the any channel which is addon or promo for any package will also be addon or promo for Entertainment
                        const entertainmentPkg = props?.packages?.find(p => DEFAULT_ADDON_PROMO_PACKAGES.includes(p.id?.toUpperCase()));
                        const packageId = entertainmentPkg ? entertainmentPkg?.id?.toUpperCase() : props?.packages[0]?.id?.toUpperCase();

                        let billingCodeLabel = "";
                        if (packageId?.includes("ATT_TV_")) {
                          // eslint-disable-next-line prefer-destructuring
                          billingCodeLabel = packageId.split("ATT_TV_")[1];
                        } else if (packageId?.includes("DTV_")) {
                          // eslint-disable-next-line prefer-destructuring
                          billingCodeLabel = packageId.split("DTV_")[1];
                        }
                        if (
                          (channelsQP === "internet" &&
                            !channel?.addonPromoChnlPackages?.includes(
                              `ATT_TV_${billingCodeLabel}`
                            )) ||
                          (channelsQP === "satellite" &&
                            !channel?.addonPromoChnlPackages?.includes(
                              `DTV_${billingCodeLabel}`
                            ))
                        ) {
                          showChannel = false;
                        }
                      }
                      return showChannel ? channel : false;
                    })
                    .filter(x => x);
                  showAccordion = commonCategoryChnlsCount?.length > 0;
                }
                if((category.key === "RSN" && props?.packages.length === 1 && ["ATT_TV_MYSPORTSBETA"]?.includes(props?.packages?.[0]?.id)) ||
                   (category.key === "LOCAL" && props?.packages.length === 1 && props?.packages?.[0]?.id === "ATT_TV_MYSPORTSBETA" && !(localMySportsGenreChannels?.length > 0))){
                  showAccordion = false
                }
                if (category.key === "INCLUDED_SUBSCRIPTIONS") {
 showAccordion = true;
}
                return (
                  showAccordion && (
                    <TableRow id="nestedTableRow" key={index}>
                      <TableCell
                        colSpan={5}
                        sx={{
                          [breakpoints.between(
                            breakpoints.values.xs,
                            breakpoints.values.xl
                          )]: {
                            fontSize: "14px",
                            padding: "0px !important"
                          },
                          "@media (min-width: 1700px)": {
                            padding: "0px !important"
                          }
                        }}
                      >
                        <Accordion
                          TransitionProps={{ unmountOnExit: true }}
                          expanded={expandAccordion[index].expanded}
                          id="tableBody"
                          key={index}
                          sx={{ ...styles.categoryAccordion }}
                          onClick={event => handleAccordionClick(event, index)}
                        >
                          {(categoryChannels?.length > 0 ||
                            category.key === "ADD_ON_CHANNELS" || category.key === "INCLUDED_SUBSCRIPTIONS") && (
                              <AccordionSummary
                                expandIcon={
                                  <CardMedia
                                    alt="Click to open dropdown"
                                    component="img"
                                    id="close-arrow"
                                    src={theme && theme?.grid?.mode === "dark" ? "https://www.directv.com/dtvassets/global/icons/2022/arrow-down_dk-bg.svg" : "https://www.directv.com/dtvassets/global/icons/fusion/arrow_jet_down.svg"}
                                    sx={{ ...styles.dropDownIcon }}
                                  />
                                }
                                // aria-controls={`nct-panel${index}-content-${nct_uniq}`}
                                aria-labelledby={`tableBody`}
                                // eslint-disable-next-line camelcase
                                id={`nct-panel${index}-header-${nct_uniq}`}
                                sx={{
                                  ...styles.accSummaryColor,
                                  ...styles.accordionSummary,
                                  minHeight: "45px !important",
                                  height: "45px !important",
                                  ...((isCommonCategory || showMyFreeSubheading) &&
                                    expandAccordion[index].expanded && {
                                      minHeight: "100%",
                                      height: "100%"
                                    }),
                                  position: "relative",
                                  "& .MuiAccordionSummary-content": {
                                    margin: {
                                      md: "12px 0  !important",
                                      sm: "9px 0 !important",
                                      xs: "9px 0 !important"
                                    }
                                  },
                                  // Mirrors the channel/number view above: with the MyFree
                                  // caveat band showing, the content's 12px vertical margin
                                  // stacks on the summary's 12px padding and doubles the gap
                                  // above the label / below the band. Padding alone owns it.
                                  ...(showMyFreeSubheading &&
                                    expandAccordion[index]?.expanded && {
                                      "& .MuiAccordionSummary-content": {
                                        margin: "0 !important"
                                      }
                                    }),
                                  ...styles.expandIconPosition,
                                  ...(latestAccordionOpen.includes(index) && {
                                    ...stickyAccordionStyles
                                  })
                                }}
                              >
                                <DtvBox
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    width: "100%"
                                  }}
                                >
                                  <DtvBox sx={{ width: "100%" }}>
                                    <Typography
                                      id={`${index}-catergoryValue`}
                                      key={`${index}-catergoryValue`}
                                      sx={{ ...styles.typography }}
                                    >
                                      {category.key === "ADD_ON_CHANNELS" ?
                                        "Add-on channels" :
                                        category.value}
                                    </Typography>
                                  </DtvBox>
                                  {showMyFreeSubheading && expandAccordion[index]?.expanded && (
                                    <MyFreeDtvSubheading
                                      content={commonData?.myFreeDtvSubheading}
                                      id={`myfreeDtv-subheading-container-category-${index}`}
                                      theme={theme}
                                    />
                                  )}
                                  <DtvBox sx={{ width: "100%" }}>
                                    {isCommonCategory &&
                                      expandAccordion[index].expanded && (
                                        <>
                                          <DtvBox
                                            id="addonPromo-info-sec"
                                            sx={
                                              isSticky &&
                                                category.key === "ADD_ON_CHANNELS" ?
                                                {
                                                  ...styles.infoSec,
                                                  marginBottom:
                                                    "8px !important"
                                                } :
                                                {
                                                  ...styles.infoSec,
                                                  marginBottom:
                                                    "8px !important"
                                                }
                                            }
                                          >
                                            <DtvBox
                                              id="included-1"
                                              sx={{
                                                ...styles.addOnDiv,
                                                [breakpoints.between(
                                                  breakpoints.values.xs,
                                                  breakpoints.values.xl
                                                )]: {
                                                  fontSize: "14px",
                                                  borderBottom: "0px !important",
                                                  display: "flex",
                                                  alignItems: "center"
                                                }
                                              }}
                                            >
                                              <CardMedia
                                                alt={"Included channels"}
                                                component="img"
                                                sx={{ ...styles.legendIcons }}
                                                src={
                                                  theme && theme?.grid?.mode === "dark" ? "/assets/content/4o3fwhj9oa/svg/included-onDark.svg" : "/assets/content/enyud976rg/svg/included-onLight.svg"
                                                }
                                              />
                                              <DtvText
                                                variant="smalltext"
                                                content={
                                                  commonData?.includedChnlLogoInfo ||
                                                  "Channel is included in package"
                                                }
                                                sx={{
 ...styles?.addOnPromoTextDiv,
color: theme && theme?.sectionHeaders?.textColor
}}
                                              />
                                            </DtvBox>
                                            <DtvBox
                                              id="add-on"
                                              sx={{
                                                ...styles.addOnDiv,
                                                [breakpoints.between(
                                                  breakpoints.values.xs,
                                                  breakpoints.values.xl
                                                )]: {
                                                  fontSize: "14px",
                                                  borderBottom: "0px !important",
                                                  display: "flex",
                                                  alignItems: "center"
                                                }
                                              }}
                                            >
                                              <CardMedia
                                                alt={"Add on channels"}
                                                component="img"
                                                sx={{ ...styles.legendIcons }}
                                                src={
                                                  theme && theme?.grid?.mode === "dark" ? "/assets/content/dvwlcmuqjk/svg/addon-onDark.svg" : "/assets/content/pr32m1cdgf/svg/addon-onLight.svg"
                                                }
                                              />
                                              <DtvText
                                                variant="smalltext"
                                                content={
                                                  commonData?.addOnChnlLogoInfo ||
                                                  "Add-on channel (Channel can be added for an additional cost)"
                                                }
                                                sx={{
 ...styles?.addOnPromoTextDiv,
color: theme && theme?.sectionHeaders?.textColor
}}
                                              />
                                            </DtvBox>
                                            <DtvBox
                                              id="promo"
                                              sx={{
                                                ...styles.promoDiv,
                                                [breakpoints.between(
                                                  breakpoints.values.xs,
                                                  breakpoints.values.xl
                                                )]: {
                                                  fontSize: "14px",
                                                  borderBottom: "0px !important",
                                                  display: "flex",
                                                  alignItems: "center"
                                                }
                                              }}
                                            >
                                              <CardMedia
                                                alt={"Promo Available"}
                                                component="img"
                                                sx={{ ...styles.legendIcons }}
                                                src={
                                                  theme && theme?.grid?.mode === "dark" ? "/assets/content/hni5xiiqjh/svg/promo-onDark.svg" : "/assets/content/joqxso5l7u/svg/promo-onLight.svg"
                                                }
                                              />
                                              <DtvText
                                                variant="smalltext"
                                                content={
                                                  commonData?.promoChnlLogoInfo ||
                                                  "Promotion (Channel is included for an introductory time)"
                                                }
                                                sx={{
 ...styles?.addOnPromoTextDiv,
color: theme && theme?.sectionHeaders?.textColor
}}
                                              />
                                            </DtvBox>
                                          </DtvBox>
                                        </>
                                      )}
                                  </DtvBox>
                                </DtvBox>
                              </AccordionSummary>
                            )}

                          <AccordionDetails
                            key={`${index}-accordionDetails`}
                            sx={{ ...styles.accordionDetails }}
                          >
                            <Typography
                              key={`${index}-catergory`}
                              sx={{
                                ...styles.typography,
                                color: "#333333",
                                width: "100%"
                              }}
                            >
                              <Table id="nestedTable">
                                <TableBody
                                  id="nestedTableBody"
                                  style={{ backgroundColor: theme && theme?.grid?.background ? theme?.grid?.background : "#ffffff" }}
                                >
                                  {category?.key === "INCLUDED_SUBSCRIPTIONS" ?
                                    getEmbeddedSvods() :
                                  <>
                                  {!isCommonCategory &&
                                    categoryChannels.map(
                                      // eslint-disable-next-line complexity
                                      (channel, channelIndex) => {
                                        const firstRow =
                                          channelIndex === 0 ?
                                            styles.tablefirstRowCat :
                                            styles.tablerowContainer;
                                        // eslint-disable-next-line camelcase
                                        const channelsQP = channelsQP_lwc || "";
                                        const highlightRow =
                                          (channel?.isSatelliteInc &&
                                            !channel?.isStreamInc) ||
                                          (!channel?.isSatelliteInc &&
                                            channel?.isStreamInc);
                                        const highLightBgRow =
                                          !isChannelLineUpPage &&
                                            highlightRow &&
                                            channelsQP === "" ?
                                            styles.highlightRowBgColor :
                                            {};
                                        const rowStyles =
                                          highlightRow && channelsQP === "" ?
                                            styles.borderHighlightAcc :
                                            {};
                                        let showChannel = true;
                                        if (channelsQP !== "") {
                                          showChannel = false;
                                          const _packageIndicators =
                                            (channel &&
                                              channel.packageIndicators &&
                                              channel.packageIndicators[
                                              `${edspFlow ?
                                                "edsp" :
                                                "2yr"
                                              }`
                                              // eslint-disable-next-line camelcase
                                              ][pkgCtgySelected_upc]) ||
                                            [];
                                          props?.packages?.forEach(
                                            (_packageItem, pkgIndex) => {
                                              const iconsInd = pkgIndex;
                                              // const packageId = props?.packages[0]?.id?.toUpperCase();
                                              const packageId =
                                                props?.packages?.length === 1 ?
                                                  props?.packages[0]?.id?.toUpperCase() :
                                                  props?.packages[
                                                    iconsInd
                                                  ]?.id?.toUpperCase();
                                              // const packageId = props?.packages[0]?.id?.toUpperCase();
                                              let billingCodeLabel = "";
                                              if (
                                                packageId?.includes("ATT_TV_")
                                              ) {
                                                // eslint-disable-next-line prefer-destructuring
                                                billingCodeLabel =
                                                  packageId.split("ATT_TV_")[1];
                                              } else if (
                                                packageId?.includes("DTV_")
                                              ) {
                                                // eslint-disable-next-line prefer-destructuring
                                                billingCodeLabel =
                                                  packageId.split("DTV_")[1];
                                              }
                                              if (
                                                (channelsQP === "internet" &&
                                                  channel?.includedChnlPackages?.includes(
                                                    `ATT_TV_${billingCodeLabel}`
                                                  )) ||
                                                (channelsQP === "satellite" &&
                                                  channel?.includedChnlPackages?.includes(
                                                    `DTV_${billingCodeLabel}`
                                                  ))
                                              ) {
                                                showChannel = true;
                                              }
                                            }
                                          );
                                        }
                                        if (
                                          channel.category === "LOCAL" ||
                                          channel.category === "RSN"
                                        ) {
                                          showChannel = true;
                                        }
                                        const accordionChannelRowElement = (
                                          <>
                                            <DtvGrid
                                              container
                                              component={TableRow}
                                              id="nestedTableRow"
                                              key={channelIndex}
                                              sx={{
                                                width: "100%",

                                                backgroundColor:
                                                  isChannelLineUpPage ?
                                                    "#102641" :
                                                    theme ? theme?.grid?.background : colorPalette.neutral_white.main,
                                                ...styles.tableBorder,
                                                ...styles.accordionRow,
                                                ...(channelIndex ===
                                                  categoryChannels?.length - 1 ?
                                                  {
                                                    firstRow,
                                                    ...styles.marginRow,
                                                    ...rowStyles,
                                                    ...styles.mb
                                                  } :
                                                  {
                                                    firstRow,
                                                    ...styles.marginRow,
                                                    ...rowStyles
                                                  })
                                              }}
                                            >
                                              {!isChannelLineUpPage &&
                                                highlightRow &&
                                                channelsQP === "" && (
                                                  <DtvBox
                                                    component={TableCell}
                                                    id="tableCell"
                                                    sx={{
                                                      padding: "0px !important",
                                                      width: "100%",
                                                      backgroundColor: theme && theme?.grid?.highLightRowBackground ? theme?.grid?.highLightRowBackground : '#D1D1D1',
                                                      color: theme && theme?.grid?.highLightRowTextColor ? `${theme?.grid?.highLightRowTextColor} !important` : "#000000",
                                                      borderBottom: `${isChannelLineUpPage ?
                                                          "1" :
                                                          "2"
                                                        }px solid ${isChannelLineUpPage ?
                                                          "#D1D1D1" :
                                                          theme && theme?.grid?.highLightRowBackground ? theme?.grid?.highLightRowBackground : "#999999"
                                                        }`,
                                                      position: "relative",
                                                      textAlign: "center",
                                                      height: {
                                                        xs: "18px",
                                                        sm: "22px"
                                                      },
                                                      lineHeight: {
                                                        xs: "1.3em",
                                                        sm: "1.7em"
                                                      },
                                                      top:
                                                        channelIndex === 0 &&
                                                        "8px",
                                                      [breakpoints.between(
                                                        breakpoints
                                                          .values.xs,
                                                        breakpoints
                                                          .values.xl
                                                      )]: {
                                                        fontSize:
                                                          "14px !important"
                                                        // padding: "0px !important"
                                                      },
                                                      "@media (min-width: 1700px)": {
                                                        height: "32px"
                                                      }
                                                    }}
                                                  >
                                                    {`Exclusively on ${channel?.isSatelliteInc ?
                                                        "Satellite" :
                                                        "Internet"
                                                      }`}
                                                  </DtvBox>
                                                )}
                                              <DtvGrid
                                                item
                                                alignItems="center"
                                                component={TableCell}
                                                id="tableCell"
                                                justifyContent="center"
                                                key={`logo${channelIndex}`}
                                                sx={{
                                                  ...styles.tableColPad,
                                                  ...styles.borderRow,
                                                  ...styles.paper,
                                                  ...highLightBgRow,
                                                  // ...styles.tableBodyRowHeight,
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  justifyContent: "center",
                                                  alignItems: "center",
                                                  ...channelColWidthStyle,
                                                  ...(detailsVisible && selectedChannel === channel.chnlNum && {
                                                    borderBottom: "none !important"
                                                  }),

                                                  [breakpoints.down(breakpoints.values.sm)]: {
                                                    minWidth: isChannelLineUpPage && {
                                                      xs: "32% !important"
                                                    }
                                                  }
                                                }}
                                              >
                                                <Tooltip
                                                  enterTouchDelay={0}
                                                  placement="top"
                                                  PopperProps={{
                                                    sx: {
                                                      marginBottom:
                                                        "-25px !important",
                                                      width: "auto !important",
                                                      fontSize:
                                                        "16px !important"
                                                    }
                                                  }}
                                                  title={getTooltipContent(
                                                    channel.name
                                                  )}
                                                >
                                                  <DtvBox component="span">
                                                    {channel.icon ? (
                                                      <>
                                                        <ChannelLogoChip
                                                          alt={channel.name}
                                                          component="span"
                                                          display="inline-flex"
                                                          imgSx={styles.icons}
                                                          margin="6px 0"
                                                          size="64px"
                                                          isOvLogo={channel?.isOvLogo}
                                                          src={channel.icon}
                                                          verticalAlign="middle"
                                                        />
                                                        {(channel?.category ===
                                                          "LOCAL" ||
                                                          channel?.category ===
                                                          "RSN") && (
                                                            <DtvText
                                                              variant="smalltext"
                                                              content={
                                                                channel?.category ===
                                                                  "LOCAL" ?
                                                                  "Local" :
                                                                  "RSN"
                                                              }
                                                              sx={{
                                                                ...styles.channelName,
                                                                color: "#FFF",
                                                                maxWidth: "55px",
                                                                maxHeight: "80px",
                                                                display: "block",
                                                                backgroundColor:
                                                                  "#555555",
                                                                borderRadius:
                                                                  "4px",
                                                                textAlign:
                                                                  "center",
                                                                m: "-4px auto 7px auto",
                                                                p: "5px",
                                                                fontSize: "12px"
                                                              }}
                                                            // tooltip={{
                                                            //   content: channel?.category === "LOCAL"  ? "Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them." : "Regional Sports Networks aren't available everywhere and may vary by package and location. You may need to be in your billing region (ZIP Code) to watch them.",
                                                            //   color: "#FFF",
                                                            //   iconSize: "12px",
                                                            //   mode: "light",
                                                            //   iconWeight: "400",
                                                            //   sx: {
                                                            //     color: "#FFF",
                                                            //     left: "5px",
                                                            //     fontWeight: "400 !important",
                                                            //     fontSize: "12px !important"
                                                            //   }
                                                            // }}
                                                            />
                                                          )}
                                                        {channel?.category === "RSN" ? <DtvText
                                                          content={`${channel?.name}<sup>${rsnChnlsObj?.[channel?.callSign]?.rsntier === 1 ? "" : rsnChnlsObj?.[channel?.callSign]?.rsntier}</sup>`}
                                                          id="channel-name-5"
                                                          variant="smalltextmedium"
                                                          sx={{
                                                            ...styles.channelName,
                                                            display: "block"
                                                          }}
                                                        /> : <DtvText
                                                          content={channel.name}
                                                          id="channel-name-1"
                                                          variant="smalltextmedium"
                                                          sx={{
                                                            ...styles.channelName,
                                                            display: "block"
                                                          }}
                                                        />}
                                                        <DtvText
                                                          variant="smalltextmedium"
                                                          content={String(
                                                            channel.chnlNum
                                                          )}
                                                          sx={{
                                                            ...styles.channelName,
                                                            ...highLightBgRow,
                                                            display: "block"
                                                          }}
                                                        />
                                                      </>
                                                    ) : (
                                                      <>
                                                        <CardMedia
                                                          alt={channel.name}
                                                          component="img"
                                                          src={`https://www.directv.com/dtvassets/global/channel-icons/m-n/Channel_Icon_No-Logo.svg`}
                                                          sx={styles.icons}
                                                        />
                                                        {channel?.category === "RSN" ? <DtvText
                                                        content={`${channel.name}`}
                                                          id="channel-name-5"
                                                          variant="smalltextmedium"
                                                          sx={{
                                                            ...styles.channelName,
                                                            display: "block"
                                                          }}
                                                        /> : <DtvText
                                                        content={channel.name}
                                                          id="channel-name-5"
                                                          variant="smalltextmedium"
                                                          sx={{
                                                            ...styles.channelName,
                                                            display: "block"
                                                          }}
                                                        />}
                                                        <DtvText
                                                          variant="smalltextmedium"
                                                          content={String(
                                                            channel.chnlNum
                                                          )}
                                                          sx={{
                                                            ...styles.channelName,
                                                            ...highLightBgRow,
                                                            display: "block"
                                                          }}
                                                        />
                                                      </>
                                                    )}
                                                  </DtvBox>
                                                </Tooltip>
                                                {(channel?.category === "LOCAL" || channel?.category === "RSN") && (
                                                  <>
                                                    <DtvButton
                                                      id={`see-details-btn-${channel.chnlNum}-${channelIndex}`}
                                                      label={"See details"}
                                                      variant="uxtext"
                                                      startIcon={
                                                        detailsVisible && selectedChannel === channel.chnlNum ?
                                                          (<DownIcon mode={theme?.grid?.mode} />) :
                                                          (<UpIcon mode={theme?.grid?.mode} />)
                                                      }
                                                      sx={{
                                                        fontSize: "12px",
                                                        fontWeight: "400",
                                                        textDecoration: "none",
                                                        "& .MuiButton-startIcon": {
                                                          margin: "0 !important"
                                                        },
                                                        margin: " 0 0 10px 0",
                                                        color: theme && theme?.grid?.textColor ? `${theme?.grid?.textColor} !important` : "#102641"
                                                      }}
                                                      onClick={(e: any) => handleClickChannel(e, channel)}
                                                    />
                                                  </>
                                                )}
                                              </DtvGrid>
                                              {(channel?.category === "LOCAL" ||
                                                channel?.category === "RSN") ?
                                                props?.packages?.map(
                                                  (packageItem, iconsInd) => {
                                                    const lastColClass =
                                                      iconsInd ===
                                                      props?.packages?.length -
                                                      1 &&
                                                      styles.tableLastCol;
                                                    const tableBodyCatCols1 =
                                                      cmprPkgIndex !== null &&
                                                        cmprPkgIndex + 1 ===
                                                        iconsInd ?
                                                        {
                                                          ...styles.tableColPadNew
                                                        } :
                                                        styles.tableColPad;
                                                    const tableBodyCatCols2 =
                                                      iconsInd === cmprPkgIndex ?
                                                        cmprPkgBorderSelected :
                                                        {};
                                                    const tableBodyCatCols = {
                                                      ...tableBodyCatCols1,
                                                      ...tableBodyCatCols2,
                                                      ...lastColClass,
                                                      ...styles.borderCol,
                                                      ...styles.borderRow
                                                    };
                                                    const singlePkg = customPkgIndex != null && props?.packages?.[0]?.name?.toLowerCase();
                                                    const categoryChannelRow = (
                                                      <DtvGrid
                                                        item
                                                        alignItems="center"
                                                        component={TableCell}
                                                        id="tableCell"
                                                        justifyContent="center"
                                                        key={iconsInd}
                                                        style={{
                                                          backgroundColor: customPkgIndex != null ? bgColors[singlePkg] :
                                                            iconsInd === cmprPkgIndex &&
                                                            !(highlightRow && channelsQP === "") &&
                                                            bgColors[props?.packages[iconsInd].name.toLowerCase()]
                                                        }}
                                                        sx={{
                                                          ...tableBodyCatCols,
                                                          ...styles.paper,
                                                          ...highLightBgRow,
                                                          // ...styles.tableBodyRowHeight,
                                                          display: "flex",
                                                          flexDirection: "column",
                                                          alignItems: "center",
                                                          justifyContent: "center",
                                                          ...mainColWidthStyle,
                                                          // [breakpoints.up(breakpoints.values.sm)]: {
                                                          //   maxWidth: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                          //   flexBasis: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                          // },
                                                          // [breakpoints.between(breakpoints.values.sm, breakpoints.values.md)]: {
                                                          //   maxWidth: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                          //   flexBasis: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                          // },
                                                          [breakpoints.down(breakpoints.values.sm)]: {
                                                            maxWidth: isChannelLineUpPage && {
                                                              xs: "17% !important"
                                                            }
                                                          },
                                                          [breakpoints.down(breakpoints.values.sm)]: {
                                                            maxWidth: "100%"
                                                          }
                                                        }}
                                                      >
                                                        <Tooltip
                                                          enterTouchDelay={0}
                                                          placement="top"
                                                          PopperProps={{
                                                            sx: {
                                                              marginBottom:
                                                                "-6px !important",
                                                              width:
                                                                "auto !important",
                                                              fontSize:
                                                                "16px !important"
                                                            }
                                                          }}
                                                          title={getTooltipContent(
                                                            `${commonData.channelIncludedText} in`,
                                                            `${props?.packages
                                                              .length === 1 ?
                                                              props
                                                                ?.packages[0]
                                                                ?.name :
                                                              props
                                                                ?.packages[
                                                                iconsInd
                                                              ]?.name
                                                            }`
                                                          )}
                                                        >
                                                          {(channel?.category === "RSN" && !channel?.packageNames?.find(pkgName => pkgName?.toLowerCase() === packageItem?.billingCodeLabel?.toLowerCase())) || (channel?.category === "LOCAL" && packageItem?.id === "ATT_TV_MYSPORTSBETA" && !localMySportsGenreChannels?.includes(channel?.name)) ? null : (
                                                            <DtvBox component="span">
                                                              <CardMedia
                                                                component="img"
                                                                alt={
                                                                  // eslint-disable-next-line no-constant-binary-expression
                                                                  `${commonData.channelIncludedText} in ${props?.packages[iconsInd]?.name}` ||
                                                                  `Included in ${props?.packages[iconsInd]?.name}`
                                                                }
                                                                src={
                                                                  theme && theme?.grid?.mode === "dark" ? "/assets/content/4o3fwhj9oa/svg/included-onDark.svg" : "/assets/content/enyud976rg/svg/included-onLight.svg"
                                                                }
                                                                sx={{
                                                                  ...styles.icons12
                                                                }}
                                                              />
                                                            </DtvBox>
                                                          )}
                                                        </Tooltip>
                                                      </DtvGrid>
                                                    );
                                                     //   WAT - 4258
                                                    // if(!msbNationalRollOut){
                                                      if((channel?.category === "RSN" && props?.packages.length === 1 && !channel?.packageNames?.find(pkgName => pkgName?.toLowerCase().includes(packageItem?.billingCodeLabel?.toLowerCase()))) || (channel?.category === "LOCAL" && props?.packages.length === 1 && packageItem?.id === "ATT_TV_MYSPORTSBETA" && !localMySportsGenreChannels?.includes(channel?.name))){
                                                        showChannel = false;
                                                      }
                                                    // }
                                                    //   WAT - 4258
                                                    if (customPackage.length === 1) {
                                                      return (
                                                        iconsInd ===
                                                        customPkgIndex &&
                                                        categoryChannelRow
                                                      );
                                                    }
                                                      return categoryChannelRow;
                                                  }
                                                )                                                :
                                                props?.packages?.map(
                                                  (packageItem, pkgIndex) => {
                                                    // WAT-11878: read the indicator from the pkg's OWN language slot so
                                                    // the cmprPkg-injected column (e.g. Óptimo Más, categoryId=SPANISH)
                                                    // doesn't inherit the current view's package's check-marks.
                                                    const packageIndicator = getPkgIndicator(channel, packageItem, !!props?.edspFlow, pkgCtgySelected_upc);
                                                    const iconsInd = pkgIndex;
                                                    const lastColClass =
                                                      iconsInd ===
                                                      props?.packages?.length -
                                                      1 &&
                                                      styles.tableLastCol;
                                                    const tableBodyCatCols1 =
                                                      cmprPkgIndex !== null &&
                                                        cmprPkgIndex + 1 ===
                                                        iconsInd ?
                                                        {
                                                          ...styles.tableColPadNew
                                                        } :
                                                        styles.tableColPad;
                                                    const tableBodyCatCols2 =
                                                      iconsInd === cmprPkgIndex ?
                                                        cmprPkgBorderSelected :
                                                        {};
                                                    const tableBodyCatCols = {
                                                      ...tableBodyCatCols1,
                                                      ...tableBodyCatCols2,
                                                      ...lastColClass,
                                                      ...styles.borderCol,
                                                      ...styles.borderRow
                                                    };
                                                    const packageId =
                                                      props?.packages?.length ===
                                                        1 ?
                                                        props?.packages[0]?.id?.toUpperCase() :
                                                        props?.packages[
                                                          iconsInd
                                                        ]?.id?.toUpperCase();
                                                    let billingCodeLabel = "";
                                                    if (!isChannelLineUpPage) {
                                                      if (
                                                        packageId?.includes(
                                                          "ATT_TV_"
                                                        )
                                                      ) {
                                                        billingCodeLabel =
                                                          packageId?.split(
                                                            "ATT_TV_"
                                                          )[1];
                                                      } else if (
                                                        packageId?.includes(
                                                          "DTV_"
                                                        )
                                                      ) {
                                                        billingCodeLabel =
                                                          packageId?.split(
                                                            "DTV_"
                                                          )[1];
                                                      }
                                                    } else {
                                                      if (
                                                        packageId?.includes(
                                                          "ATT_TV_"
                                                        )
                                                      ) {
                                                        billingCodeLabel =
                                                          packageId?.split(
                                                            "ATT_TV_"
                                                          )[1];
                                                      } else if (
                                                        packageId?.includes(
                                                          "DTV_"
                                                        )
                                                      ) {
                                                        billingCodeLabel =
                                                          packageId?.split(
                                                            "DTV_"
                                                          )[1];
                                                      }
                                                    }
                                                    const highlightPkg =
                                                      (channel?.includedChnlPackages?.includes(
                                                        `ATT_TV_${billingCodeLabel}`
                                                      ) &&
                                                        !channel?.includedChnlPackages?.includes(
                                                          `DTV_${billingCodeLabel}`
                                                        )) ||
                                                      (!channel?.includedChnlPackages?.includes(
                                                        `ATT_TV_${billingCodeLabel}`
                                                      ) &&
                                                        channel?.includedChnlPackages?.includes(
                                                          `DTV_${billingCodeLabel}`
                                                        ));
                                                    const labelText =
                                                      !channel?.includedChnlPackages?.includes(
                                                        `ATT_TV_${billingCodeLabel}`
                                                      ) &&
                                                        channel?.includedChnlPackages?.includes(
                                                          `DTV_${billingCodeLabel}`
                                                        ) ?
                                                        "Satellite Only" :
                                                        "Internet Only";
                                                    let showPkg = true;
                                                    if (channelsQP !== "") {
                                                      if (!isChannelLineUpPage) {
                                                        if (
                                                          (channelsQP ===
                                                            "internet" &&
                                                            !channel?.includedChnlPackages?.includes(
                                                              `ATT_TV_${billingCodeLabel}`
                                                            )) ||
                                                          (channelsQP ===
                                                            "satellite" &&
                                                            !channel?.includedChnlPackages?.includes(
                                                              `DTV_${billingCodeLabel}`
                                                            ))
                                                        ) {
                                                          showPkg = false;
                                                        }
                                                      } else {
                                                        if (
                                                          (channelsQP ===
                                                            "internet" &&
                                                            !channel?.includedChnlPackages?.includes(
                                                              `ATT_TV_${billingCodeLabel}`
                                                            )) ||
                                                          (channelsQP ===
                                                            "satellite" &&
                                                            !channel?.includedChnlPackages?.includes(
                                                              `DTV_${billingCodeLabel}`
                                                            ))
                                                        ) {
                                                          showPkg = false;
                                                        }
                                                      }
                                                      if (
                                                        channel.category ===
                                                        "LOCAL" ||
                                                        channel.category === "RSN"
                                                      ) {
 showPkg = true;
}
                                                    }
                                                    const _highlightLabel =
                                                      !highlightRow &&
                                                      highlightPkg &&
                                                      channelsQP !==
                                                      labelText?.toLowerCase();
                                                    const singlePkg = customPkgIndex != null && props?.packages?.[0]?.name?.toLowerCase();
                                                    const categoryChannelRow = (
                                                      <DtvGrid
                                                        item
                                                        alignItems="center"
                                                        component={TableCell}
                                                        id="tableCell"
                                                        justifyContent="center"
                                                        key={iconsInd}
                                                        style={{
                                                          backgroundColor: customPkgIndex != null ? bgColors[singlePkg] :
                                                            iconsInd === cmprPkgIndex &&
                                                            !(highlightRow && channelsQP === "") &&
                                                            bgColors[props?.packages[iconsInd].name.toLowerCase()]
                                                        }}
                                                        sx={{
                                                          ...tableBodyCatCols,
                                                          ...styles.paper,
                                                          ...highLightBgRow,
                                                          // ...styles.tableBodyRowHeight,
                                                          display: "flex",
                                                          flexDirection: "column",
                                                          alignItems: "center",
                                                          justifyContent: "center",
                                                          ...mainColWidthStyle,
                                                          // [breakpoints.up(breakpoints.values.sm)]: {
                                                          //   maxWidth: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                          //   flexBasis: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                          // },
                                                          // [breakpoints.between(breakpoints.values.sm, breakpoints.values.md)]: {
                                                          //   maxWidth: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                          //   flexBasis: customPkgIndex != null ? "85%!important" : "calc((100% - 116px)/4) !important" ,
                                                          // },
                                                          [breakpoints.down(breakpoints.values.sm)]: {
                                                            maxWidth: isChannelLineUpPage && {
                                                              xs: "17% !important"
                                                            }
                                                          },
                                                          [breakpoints.down(breakpoints.values.sm)]: {
                                                            maxWidth: "100%"
                                                          }
                                                        }}
                                                      >
                                                        {packageIndicator === PACKAGE_INDICATORS.IN && showPkg && (
                                                          <Tooltip
                                                            enterTouchDelay={0}
                                                            placement="top"
                                                            PopperProps={{
                                                              sx: {
                                                                marginBottom: "-6px !important",
                                                                width: "auto !important",
                                                                fontSize: "16px !important"
                                                              }
                                                            }}
                                                            title={getTooltipContent(
                                                              `${commonData.channelIncludedText} in`,
                                                              `${props?.packages.length === 1 ?
                                                                props?.packages[0]?.name :
                                                                props?.packages[iconsInd]?.name
                                                              }`
                                                            )}
                                                          >
                                                            <DtvBox component="span">
                                                              <CardMedia
                                                                component="img"
                                                                alt={
                                                                  // eslint-disable-next-line no-constant-binary-expression
                                                                  `${commonData.channelIncludedText} in ${props?.packages[iconsInd]?.name}` ||
                                                                  `Included in ${props?.packages[iconsInd]?.name}`
                                                                }
                                                                src={
                                                                  theme && theme?.grid?.mode === "dark" ? "/assets/content/4o3fwhj9oa/svg/included-onDark.svg" : "/assets/content/enyud976rg/svg/included-onLight.svg"
                                                                }
                                                                sx={{
                                                                  ...styles.icons12
                                                                }}
                                                              />
                                                            </DtvBox>
                                                          </Tooltip>
                                                        )}
                                                      </DtvGrid>
                                                    );
                                                    if (customPackage.length === 1) {
                                                      return (
                                                        iconsInd ===
                                                        customPkgIndex &&
                                                        categoryChannelRow
                                                      );
                                                    }
                                                      return categoryChannelRow;
                                                  }
                                                )}
                                            </DtvGrid>
                                            {(channel?.category === "LOCAL" ||
                                              channel?.category === "RSN") &&
                                              detailsVisible &&
                                              selectedChannel ===
                                              channel.chnlNum && (
                                                <DtvGrid
                                                  container
                                                  component={TableRow}
                                                  id={`row-${channel.chnlNum}-${channelIndex}-see-detials-section`}
                                                  key={`see-detail-${channelIndex}`}
                                                  sx={{ width: "100%" }}
                                                >
                                                  <TableCell
                                                    colSpan={5}
                                                    sx={{
                                                      border: "none !important",
                                                      borderBottom:
                                                        "1px solid #102641 !important",
                                                      padding: "0px !important",
                                                      width: "100%"
                                                    }}
                                                  >
                                                    {
                                                      <DtvBox
                                                        sx={{
                                                          // position: 'absolute',

                                                          // left: "0px",
                                                          // bottom : "0",
                                                          background: "white"

                                                          // width : `${tableRef?.current?.offsetWidth}px` || 0
                                                        }}
                                                      >
                                                        {channel?.category ===
                                                          "LOCAL" ? (
                                                          <DtvText
                                                            content={
                                                              "Local channels aren't available everywhere and may vary by location. You may need to be in your billing region (ZIP Code) to watch them."
                                                            }
                                                            sx={{
                                                              padding:
                                                                "4px 0 4px 0",
                                                              fontFamily:
                                                                "roboto-regular",
                                                              fontSize:
                                                                "12px !important",
                                                              paddingLeft:
                                                                // eslint-disable-next-line no-constant-binary-expression
                                                                `${document
                                                                  .getElementById(
                                                                    `see-details-btn-${channel.chnlNum}-${channelIndex}`
                                                                  )
                                                                  ?.getBoundingClientRect?.()
                                                                  ?.x
                                                                }px` || "20px"
                                                            }}
                                                          />
                                                        ) : (
                                                          <>
                                                            {channel?.mlb ||
                                                              channel?.nba ||
                                                              channel?.nhl ||
                                                              channel?.mls || channel?.wnba ? (
                                                              <DtvBox
                                                                sx={{
                                                                  ...styles.teamsContainer,
                                                                  paddingLeft:
                                                                    // eslint-disable-next-line no-constant-binary-expression
                                                                    `${document
                                                                      .getElementById(
                                                                        `see-details-btn-${channel.chnlNum}-${channelIndex}`
                                                                      )
                                                                      ?.getBoundingClientRect?.()
                                                                      ?.x
                                                                    }px` ||
                                                                    "20px"
                                                                }}
                                                              >
                                                                <DtvText
                                                                  content="Teams:"
                                                                  variant={
                                                                    "body3medium"
                                                                  }
                                                                ></DtvText>
                                                                <DtvBox
                                                                  component="span"
                                                                  sx={{
                                                                    color:
                                                                      "white",
                                                                    // margin:
                                                                    //   "0 0 0 16px",
                                                                      ...styles.teamsContainer,
                                                                  paddingLeft:
                                                                    // eslint-disable-next-line no-constant-binary-expression
                                                                    `${document
                                                                      .getElementById(
                                                                        `see-details-btn-${channel.chnlNum}-${channelIndex}`
                                                                      )
                                                                      ?.getBoundingClientRect?.()
                                                                      ?.x
                                                                    }px` ||
                                                                    "20px"
                                                                  }}
                                                                >
                                                                    <DtvText
                                                                      content={getRsnTeams(channel)}
                                                                      id="mlb-team"
                                                                      variant="body3"
                                                                    />
                                                                </DtvBox>
                                                              </DtvBox>
                                                            ) : null}
                                                            <DtvText
                                                              content={`<sup>${rsnChnlsObj?.[channel?.callSign]?.rsntier === 1 ? "" : rsnChnlsObj?.[channel?.callSign]?.rsntier}</sup> ${rsnTierLegalTextObj?.[0]?.[props?.channelsQP?.toLowerCase() === "satellite" ? "satellite" : "stream"]?.[`tier${rsnChnlsObj?.[channel?.callSign]?.rsntier}`]}`}
                                                              sx={{
                                                                padding:
                                                                  "10px 0 10px 0",
                                                                fontFamily:
                                                                  "roboto-regular",
                                                                fontSize:
                                                                  "12px !important",
                                                                paddingLeft:
                                                                  // eslint-disable-next-line no-constant-binary-expression
                                                                  `${document
                                                                    .getElementById(
                                                                      `see-details-btn-${channel.chnlNum}-${channelIndex}`
                                                                    )
                                                                    ?.getBoundingClientRect?.()
                                                                    ?.x
                                                                  }px` ||
                                                                  "20px"
                                                              }}
                                                            />
                                                          </>
                                                        )}
                                                      </DtvBox>
                                                    }
                                                  </TableCell>
                                                </DtvGrid>
                                              )}
                                          </>
                                        );
                                        if (showChannel) {
                                          if (channelIndex <= 15) {
                                            return (
                                              <>{accordionChannelRowElement}</>
                                            );
                                          }
                                            return (
                                              <LazyLoad
                                                height={"60px"}
                                                key={channelIndex}
                                                offset={500}
                                                once={true}
                                              >
                                                {accordionChannelRowElement}
                                              </LazyLoad>
                                            );
                                        }
                                        return null;
                                      }
                                    )}

                                  {isCommonCategory && Object.keys(commonCategory).map((ctgry, cmmnCtgIndex) => {
                                    try {
                                      const category = props?.channelCategories.find(item => item.key === ctgry);
                                      return (
                                        <>
                                          {commonCategory?.[ctgry]?.length > 0 && (
                                            <TableRow id="nestedTableRow" key={cmmnCtgIndex}>
                                              <TableCell
                                                id="tableCell"
                                                sx={{
                                                  [breakpoints.between(breakpoints.values.xs, breakpoints.values.xl)]: {
                                                    fontSize: "14px !important",
                                                    padding: "0px !important"
                                                  }
                                                }}
                                              >
                                                <DtvText
                                                  component="div"
                                                  content={category.value}
                                                  id="category-inside-addon-sec"
                                                  variant="smalltext"
                                                  sx={{
                                                    ...styles.addOnPkgName,
                                                    ...styles.borderRow
                                                  }}
                                                />
                                              </TableCell>
                                            </TableRow>
                                          )}

                                          {commonCategory[ctgry].map((channel, channelIndex) => {
                                            const firstRow =
                                              channelIndex === 0 ?
                                                styles.tablefirstRowCat :
                                                styles.tablerowContainer;
                                            // eslint-disable-next-line camelcase
                                            const channelsQP = channelsQP_lwc || "";
                                            const highlightRow =
                                              (channel?.isSatelliteAddonPromo &&
                                                !channel?.isStreamAddonPromo) ||
                                              (!channel?.isSatelliteAddonPromo &&
                                                channel?.isStreamAddonPromo);
                                            const highLightBgRow =
                                              !isChannelLineUpPage &&
                                                highlightRow &&
                                                channelsQP === "" ?
                                                styles.highlightRowBgColor :
                                                {};
                                            let showChannel = true;
                                            if (channelsQP !== "") {
                                              // NOTE: Considering the any channel which is addon or promo for any package will also be addon or promo for Entertainment
                                              const entertainmentPkg = props?.packages?.find(p => DEFAULT_ADDON_PROMO_PACKAGES.includes(p.id?.toUpperCase()));
                                              const packageId = entertainmentPkg ? entertainmentPkg?.id?.toUpperCase() : props?.packages[0]?.id?.toUpperCase();

                                              let billingCodeLabel = "";
                                              if (
                                                packageId?.includes(
                                                  "ATT_TV_"
                                                )
                                              ) {
                                                // eslint-disable-next-line prefer-destructuring
                                                billingCodeLabel =
                                                  packageId.split(
                                                    "ATT_TV_"
                                                  )[1];
                                              } else if (
                                                packageId?.includes(
                                                  "DTV_"
                                                )
                                              ) {
                                                // eslint-disable-next-line prefer-destructuring
                                                billingCodeLabel =
                                                  packageId.split(
                                                    "DTV_"
                                                  )[1];
                                              }
                                              if (
                                                (channelsQP ===
                                                  "internet" &&
                                                  !channel?.addonPromoChnlPackages?.includes(
                                                    `ATT_TV_${billingCodeLabel}`
                                                  )) ||
                                                (channelsQP ===
                                                  "satellite" &&
                                                  !channel?.addonPromoChnlPackages?.includes(
                                                    `DTV_${billingCodeLabel}`
                                                  ))
                                              ) {
                                                showChannel = false;
                                              }
                                            }
                                            const accordionChannelRowElement =
                                              (
                                                <DtvGrid
                                                  container
                                                  component={TableRow}
                                                  id="nestedTableRow"
                                                  key={channelIndex}
                                                  sx={{
                                                    width: "100%",

                                                    backgroundColor:
                                                      isChannelLineUpPage ?
                                                        "#21528C" :
                                                        colorPalette.neutral_white.main,
                                                    ...styles.tableBorder,
                                                    ...styles.accordionRow,
                                                    firstRow,
                                                    ...styles.marginRow
                                                  }}
                                                >
                                                  {!isChannelLineUpPage &&
                                                    highlightRow &&
                                                    channelsQP === "" && (
                                                      <DtvBox
                                                        component={TableCell}
                                                        id="tableCell"
                                                        sx={{
                                                          padding: "0px !important",
                                                          width: "100%",
                                                          backgroundColor: theme && theme?.grid?.highLightRowBackground ? theme?.grid?.highLightRowBackground : '#DADADA',
                                                          color: theme && theme?.grid?.highLightRowTextColor ? `${theme?.grid?.highLightRowTextColor} !important` : "#000000",
                                                          borderBottom: `${isChannelLineUpPage ? "1" : "2"}px solid ${isChannelLineUpPage ? "#D1D1D1" : theme && theme?.grid?.highLightRowBackground ? theme?.grid?.highLightRowBackground : "#999999"
                                                            }`,
                                                          position: "relative",
                                                          textAlign: "center",
                                                          height: {
                                                            xs: "18px",
                                                            sm: "22px"
                                                          },
                                                          lineHeight: {
                                                            xs: "1.3em",
                                                            sm: "1.7em"
                                                          },
                                                          top:
                                                            channelIndex ===
                                                            0 && "8px",
                                                          [breakpoints.between(DtvTheme.breakpoints.values.xs, DtvTheme.breakpoints.values.xl)]: {
                                                            fontSize: "14px !important"
                                                          },
                                                          "@media (min-width: 1700px)": {
                                                            height: "32px"
                                                          }
                                                        }}
                                                      >
                                                        {`Exclusively on ${channel?.isSatelliteAddonPromo ? "Satellite" : "Internet"}`}
                                                      </DtvBox>
                                                    )}
                                                  <DtvGrid
                                                    item
                                                    alignItems="center"
                                                    component={TableCell}
                                                    id="tableCell"
                                                    justifyContent="center"
                                                    key={`logo${channelIndex}`}
                                                    sx={{
                                                      ...styles.tableColPad,
                                                      ...styles.borderRow,
                                                      ...styles.paper,
                                                      ...highLightBgRow,
                                                      // ...styles.tableBodyRowHeight,
                                                      display: "flex",
                                                      flexDirection: "column",
                                                      justifyContent: "center",
                                                      alignItems: "center",
                                                      ...channelColWidthStyle,
                                                      [breakpoints.down(DtvTheme.breakpoints.values.sm)]: {
                                                        minWidth: isChannelLineUpPage && {
                                                          xs: "32% !important"
                                                        }
                                                      }
                                                    }}
                                                  >
                                                    <Tooltip
                                                      enterTouchDelay={0}
                                                      placement="top"
                                                      title={getTooltipContent(channel.name)}
                                                      PopperProps={{
                                                        sx: {
                                                          marginBottom: "-25px !important",
                                                          width: "auto !important",
                                                          fontSize: "16px !important"
                                                        }
                                                      }}
                                                    >
                                                      <DtvBox component="span">
                                                        {channel.icon ? (
                                                          <>
                                                            <ChannelLogoChip
                                                              alt={channel.name}
                                                              component="span"
                                                              display="inline-flex"
                                                              imgSx={styles.icons}
                                                              margin="6px 0"
                                                              size="64px"
                                                              isOvLogo={channel?.isOvLogo}
                                                              src={channel.icon}
                                                              verticalAlign="middle"
                                                            />
                                                            <DtvText
                                                            content={channel.name}
                                                              id="channel-name-3"
                                                              variant="smalltextmedium"
                                                              sx={{
                                                                ...styles.channelName,
                                                                display: "block"
                                                              }}
                                                            />
                                                            <DtvText
                                                              variant="smalltextmedium"
                                                              content={String(
                                                                channel.chnlNum
                                                              )}
                                                              sx={{
                                                                ...styles.channelName,
                                                                ...highLightBgRow,
                                                                display: "block"
                                                              }}
                                                            />
                                                          </>
                                                        ) : (
                                                          <>
                                                            <CardMedia
                                                              component="img"
                                                              src={`https://www.directv.com/dtvassets/global/channel-icons/m-n/Channel_Icon_No-Logo.svg`}
                                                              alt={
                                                                channel.name
                                                              }
                                                              sx={
                                                                styles.icons
                                                              }
                                                            />
                                                            <DtvText
                                                              variant="smalltextmedium"
                                                              content={
                                                                channel.name
                                                              }
                                                              sx={{
                                                                ...styles.channelName,
                                                                display: "block"
                                                              }}
                                                            />
                                                            <DtvText
                                                              variant="smalltextmedium"
                                                              content={String(
                                                                channel.chnlNum
                                                              )}
                                                              sx={{
                                                                ...styles.channelName,
                                                                ...highLightBgRow,
                                                                display:
                                                                  "block"
                                                              }}
                                                            />
                                                          </>
                                                        )}
                                                      </DtvBox>
                                                    </Tooltip>
                                                  </DtvGrid>
                                                  {props?.packages?.map(
                                                    (packageItem, pkgIndex) => {
                                                      // WAT-11878: add-on row — read from `addOnPkgIndicators` AND from the
                                                      // pkg's own language slot so the cmprPkg-injected column doesn't
                                                      // silently render as not-included.
                                                      const packageIndicator = getPkgIndicator(channel, packageItem, !!props?.edspFlow, pkgCtgySelected_upc, "addon");
                                                      const iconsInd = pkgIndex;
                                                      // Fix WAT-11878 round-4: use props.packages.length (column count),
                                                      // not the addon indicator array length — the two need not agree once
                                                      // an injected column carries no addon indicator, and the deleted
                                                      // `packageIndicators` local made the previous read wrong to begin with.
                                                      const lastColClass = iconsInd === (props?.packages?.length ?? 0) - 1 &&
                                                        styles.tableLastCol;
                                                      const tableBodyCatCols1 =
                                                        cmprPkgIndex !==
                                                          null &&
                                                          cmprPkgIndex +
                                                          1 ===
                                                          iconsInd ?
                                                          {
                                                            ...styles.tableColPadNew
                                                          } :
                                                          styles.tableColPad;
                                                      const tableBodyCatCols2 =
                                                        iconsInd ===
                                                          cmprPkgIndex ?
                                                          cmprPkgBorderSelected :
                                                          {};
                                                      const tableBodyCatCols =
                                                      {
                                                        ...tableBodyCatCols1,
                                                        ...tableBodyCatCols2,
                                                        ...lastColClass,
                                                        ...styles.borderCol,
                                                        ...styles.borderRow
                                                      };
                                                      const packageId =
                                                        props?.packages
                                                          ?.length === 1 ?
                                                          props?.packages[0]?.id?.toUpperCase() :
                                                          props?.packages[
                                                            iconsInd
                                                          ]?.id?.toUpperCase();
                                                      let billingCodeLabel = "";
                                                      if (!isChannelLineUpPage) {
                                                        if (packageId?.includes("ATT_TV_")
                                                        ) {
                                                          billingCodeLabel = packageId?.split("ATT_TV_")[1];
                                                        } else if (
                                                          packageId?.includes(
                                                            "DTV_"
                                                          )
                                                        ) {
                                                          billingCodeLabel =
                                                            packageId?.split(
                                                              "DTV_"
                                                            )[1];
                                                        }
                                                      } else {
                                                        if (
                                                          packageId?.includes(
                                                            "ATT_TV_"
                                                          )
                                                        ) {
                                                          billingCodeLabel =
                                                            packageId?.split(
                                                              "ATT_TV_"
                                                            )[1];
                                                        }
                                                      }
                                                      const highlightPkg =
                                                        (channel?.addonPromoChnlPackages?.includes(
                                                          `ATT_TV_${billingCodeLabel}`
                                                        ) &&
                                                          !channel?.addonPromoChnlPackages?.includes(
                                                            `DTV_${billingCodeLabel}`
                                                          )) ||
                                                        (!channel?.addonPromoChnlPackages?.includes(
                                                          `ATT_TV_${billingCodeLabel}`
                                                        ) &&
                                                          channel?.addonPromoChnlPackages?.includes(
                                                            `DTV_${billingCodeLabel}`
                                                          ));
                                                      const labelText =
                                                        !channel?.addonPromoChnlPackages?.includes(
                                                          `ATT_TV_${billingCodeLabel}`
                                                        ) &&
                                                          channel?.addonPromoChnlPackages?.includes(
                                                            `DTV_${billingCodeLabel}`
                                                          ) ?
                                                          "Satellite Only" :
                                                          "Internet Only";
                                                      const highlightLabel =
                                                        !highlightRow &&
                                                        highlightPkg &&
                                                        channelsQP !==
                                                        labelText?.toLowerCase();
                                                      const singlePkg = customPkgIndex != null && props?.packages?.[0]?.name?.toLowerCase();
                                                      const categoryChannelRow =
                                                        (
                                                          <DtvGrid
                                                            item
                                                            alignItems="center"
                                                            component={TableCell}
                                                            id="tableCell"
                                                            justifyContent="center"
                                                            key={iconsInd}
                                                            style={{
                                                              backgroundColor: customPkgIndex != null ? bgColors[singlePkg] :
                                                                iconsInd ===
                                                                cmprPkgIndex &&
                                                                !(
                                                                  highlightRow &&
                                                                  channelsQP ===
                                                                  ""
                                                                ) &&
                                                                bgColors[
                                                                props?.packages[
                                                                  iconsInd
                                                                ].name.toLowerCase()
                                                                ]
                                                            }}
                                                            sx={{
                                                              ...tableBodyCatCols,
                                                              ...styles.paper,
                                                              ...highLightBgRow,
                                                              // ...styles.tableBodyRowHeight,
                                                              display: "flex",
                                                              flexDirection: "column",
                                                              justifyContent: "center",
                                                              alignItems: "center",
                                                              ...mainColWidthStyle,
                                                              // [breakpoints.up(breakpoints.values.sm)]: {
                                                              //   maxWidth: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                              //   flexBasis: customPkgIndex != null ? "90%!important" : `calc((100% - 116px)/4) !important`,
                                                              // },
                                                              // [breakpoints.between(breakpoints.values.sm, breakpoints.values.md)]: {
                                                              //   maxWidth: customPkgIndex != null ? "85%!important" : `calc((100% - 116px)/4) !important` ,
                                                              //   flexBasis: customPkgIndex != null ? "85%!important" : `calc((100% - 116px)/4) !important` ,
                                                              // },
                                                              [breakpoints.down(
                                                                DtvTheme.breakpoints.values.sm
)]: {
                                                                maxWidth: isChannelLineUpPage && {
                                                                  xs: "17% !important"
                                                                }
                                                              },
                                                              [breakpoints.down(
                                                                DtvTheme.breakpoints.values.sm
)]: {
                                                                maxWidth: "100%"
                                                              }
                                                            }}
                                                          >
                                                            {packageIndicator === PACKAGE_INDICATORS.IN && (
                                                              <Tooltip
                                                                id="test-icon-123"
                                                                placement="top"
                                                                PopperProps={{
                                                                  sx: {
                                                                    marginBottom:
                                                                      "-6px !important",
                                                                    width:
                                                                      "auto !important",
                                                                    fontSize:
                                                                      "16px !important"
                                                                  }
                                                                }}
                                                                enterTouchDelay={
                                                                  0
                                                                }
                                                                title={getTooltipContent(
                                                                  `${commonData.channelIncludedText} in`,
                                                                  `${props?.packages[iconsInd]?.name}`
                                                                )}
                                                              >
                                                                <DtvBox
                                                                  sx={{
                                                                    display:
                                                                      highlightLabel &&
                                                                      "inline-block",
                                                                    paddingTop:
                                                                      highlightLabel &&
                                                                      "22px"
                                                                  }}
                                                                >
                                                                  <CardMedia
                                                                    component="img"
                                                                    alt={
                                                                      // eslint-disable-next-line no-constant-binary-expression
                                                                      `${commonData.channelIncludedText} in ${props?.packages[iconsInd]?.name}` ||
                                                                      `Included in ${props?.packages[iconsInd]?.name}`
                                                                    }
                                                                    src={
                                                                      theme && theme?.grid?.mode === "dark" ? "/assets/content/4o3fwhj9oa/svg/included-onDark.svg" : "/assets/content/enyud976rg/svg/included-onLight.svg"
                                                                    }
                                                                    sx={{
                                                                      ...styles.icons12
                                                                    }}
                                                                  />

                                                                </DtvBox>
                                                              </Tooltip>
                                                            )}
                                                            {packageIndicator ===
                                                              PACKAGE_INDICATORS.ADDON && (
                                                                <Tooltip
                                                                  placement="top"
                                                                  PopperProps={{
                                                                    sx: {
                                                                      marginBottom:
                                                                        "-6px !important",
                                                                      width:
                                                                        "auto !important",
                                                                      fontSize:
                                                                        "16px !important"
                                                                    }
                                                                  }}
                                                                  enterTouchDelay={
                                                                    0
                                                                  }
                                                                  title={getTooltipContent(
                                                                    `${commonData.channelAddOntext} in`,
                                                                    `${props?.packages[iconsInd]?.name}`
                                                                  )}
                                                                >
                                                                  <DtvBox
                                                                    sx={{
                                                                      display:
                                                                        highlightLabel &&
                                                                        "inline-block",
                                                                      paddingTop:
                                                                        highlightLabel &&
                                                                        "22px"
                                                                    }}
                                                                  >
                                                                    <CardMedia
                                                                      component="img"
                                                                      alt={
                                                                        // eslint-disable-next-line no-constant-binary-expression
                                                                        `${commonData.channelAddOntext} in ${props?.packages[iconsInd]?.name}` ||
                                                                        `Premium channel in ${props?.packages[iconsInd]?.name}`
                                                                      }
                                                                      src={
                                                                        "https://www.directv.com/dtvassets/global/icons/component-controls/icons-in-table_dollar.svg"
                                                                      }
                                                                      sx={{
                                                                        ...styles.icons12
                                                                      }}
                                                                    />

                                                                  </DtvBox>
                                                                </Tooltip>
                                                              )}
                                                            {packageIndicator ===
                                                              PACKAGE_INDICATORS.PROMO && (
                                                                <Tooltip
                                                                  placement="top"
                                                                  PopperProps={{
                                                                    sx: {
                                                                      marginBottom:
                                                                        "-6px !important",
                                                                      width:
                                                                        "auto !important",
                                                                      fontSize:
                                                                        "16px !important"
                                                                    }
                                                                  }}
                                                                  enterTouchDelay={
                                                                    0
                                                                  }
                                                                  title={getTooltipContent(
                                                                    `${commonData.channelPromoText} in`,
                                                                    `${props?.packages[iconsInd]?.name}`
                                                                  )}
                                                                >
                                                                  <DtvBox
                                                                    sx={{
                                                                      display:
                                                                        highlightLabel &&
                                                                        "inline-block",
                                                                      paddingTop:
                                                                        highlightLabel &&
                                                                        "22px"
                                                                    }}
                                                                  >
                                                                    <CardMedia
                                                                      component="img"
                                                                      alt={
                                                                        // eslint-disable-next-line no-constant-binary-expression
                                                                        `${commonData.channelPromoText} in ${props?.packages[iconsInd]?.name}` ||
                                                                        `Promo available in ${props?.packages[iconsInd]?.name}`
                                                                      }
                                                                      src={
                                                                        "https://www.directv.com/dtvassets/global/icons/component-controls/icons-in-table_promotion.svg"
                                                                      }
                                                                      sx={{
                                                                        ...styles.icons12
                                                                      }}
                                                                    />

                                                                  </DtvBox>
                                                                </Tooltip>
                                                              )}
                                                          </DtvGrid>
                                                        );
                                                      if (customPackage.length === 1) {
                                                        return (
                                                          iconsInd === customPkgIndex && categoryChannelRow
                                                        );
                                                      }
                                                        return categoryChannelRow;
                                                    }
                                                  )}
                                                </DtvGrid>
                                              );
                                            if (showChannel) {
                                              if (channelIndex <= 15) {
                                                return (
                                                  <>
                                                    {
                                                      accordionChannelRowElement
                                                    }
                                                  </>
                                                );
                                              }
                                                return (
                                                  <LazyLoad
                                                    height={"60px"}
                                                    key={channelIndex}
                                                    offset={500}
                                                    once={true}
                                                  >
                                                    {
                                                      accordionChannelRowElement
                                                    }
                                                  </LazyLoad>
                                                );
                                            }
                                            return null;
                                          })}
                                        </>
                                      );
                                    } catch (error) {
                                      console.log("error rendering channel grid inside", error);
                                      // return <ErrorDisplay error={error} />;
                                      return <></>;
                                    }
                                  })}
                                  </>}
                                </TableBody>
                              </Table>
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      </TableCell>
                    </TableRow>
                  )
                );
              }
            })}
        </TableBody>
      </Table>
    );
  } catch (error) {
    console.log("error rendering channel grid", error);
    // return <ErrorDisplay error={error} />;
    return <></>;
  }
}
